// ---- Content schemas (zod) ----
// The single source of truth for every content/*.json document shape. Each
// domain gets one schema here; `bun run gen` exports them as JSON Schemas into
// content/schema/*.schema.json (editor/AI documentation), and validate.ts runs
// them structurally at load time (server boot, client bundle init, studio PUT).
//
// Keep this module dependency-free beyond zod (plus src/model/rig.ts and
// src/anim/archetypes.ts, which are pure data — type-only THREE / no imports): it
// is imported by the headless server, the browser bundle, the studio server, and
// the gen script.

import { z } from "zod";
import { RIGS } from "../model/rig.ts";
import { WEAPON_ARCHETYPE_IDS } from "../anim/archetypes.ts";
import { BEARD_STYLES, BODY_BUILDS, FACE_EXPRESSIONS, HAIR_STYLES, SEXES } from "../shared.ts";

// Shared field fragments -----------------------------------------------------

/** Stable content id: lowercase kebab (matches existing quest/npc/item ids). */
export const idField = z.string().regex(/^[a-z0-9][a-z0-9-]*$/, "lowercase kebab-case id");

/** Every directory under content/ — the single list the codegen scan
 *  (scripts/gen-content.ts) and the manifest staleness check (load.ts) share.
 *  Adding a content domain = extend this + SCHEMAS below. */
export const CONTENT_DIRS = ["quests", "npcs", "items", "models", "decos", "parts", "structures", "shops", "skills", "standing", "moves", "scenarios", "cinematics", "catch-tables", "scatter-palettes", "tunables", "maps", "world", "anim"] as const;

/** The attack-clip styles (content/anim/clips.json keys) — shared by the anim
 *  schema, skill grants, and move `clip` refs. */
const ATTACK_STYLES = ["punch", "swing", "chop", "thrust", "draw"] as const;

// Quests ----------------------------------------------------------------------
// Mirrors QuestSpec in src/quest/define.ts exactly.

/** Shared "hold N of these item types" block — used by item + deliver objectives.
 *  `types` is OR-matched (any listed type counts toward `count`). */
const QuestItemReqSchema = {
  types: z.array(z.string().min(1)).min(1).describe("acceptable item types (OR — any counts)"),
  count: z.number().int().positive().optional().describe("units required across those types (default 1)"),
};

export const QuestObjectiveSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("kill"), npcId: z.string().min(1).describe("NPC SPAWN id — auto-completes on the killing blow") }),
  z.strictObject({ kind: z.literal("talk"), npcId: z.string().min(1).describe("NPC SPAWN id — completes via an advanceQuest dialogue choice") }),
  z.strictObject({
    kind: z.literal("item"),
    ...QuestItemReqSchema,
  }).describe("auto-completes when the bag holds count of any listed type"),
  z.strictObject({
    kind: z.literal("deliver"),
    npcId: z.string().min(1).describe("NPC SPAWN id — advanceQuest while talking + holding items"),
    ...QuestItemReqSchema,
    consume: z.boolean().optional().describe("remove the items on turn-in (default true)"),
  }),
  z.strictObject({ kind: z.literal("custom"), tag: z.string().min(1) }),
]);

/** Optional per-step tutorial coach (client-only presentation). */
export const QuestCoachSchema = z.strictObject({
  equip: z.strictObject({
    type: z.string().min(1).describe("item type to equip"),
    as: z.enum(["tool", "weapon"]).optional().describe("equip channel (default tool — rods)"),
  }).optional().describe("glow bag + item until this is equipped"),
  select: z.strictObject({
    type: z.string().min(1).describe("item type to select in the bag (no equip)"),
  }).optional().describe("glow bag + item until this is selected (kindling → Start fire)"),
  point: z.strictObject({
    x: z.number(),
    z: z.number(),
    label: z.string().optional(),
    map: z.string().min(1).optional().describe("only show this point while ACTIVE_MAP_ID matches (cross-map edge guides)"),
  }).optional().describe("world xz for the yellow coach arrow (wins over pointWater / pointNearestChop)"),
  pointWater: z.string().min(1).optional().describe("map waters[].id — resolves arrow from the active map when point is omitted"),
  pointNearestChop: z.boolean().optional().describe("point at the nearest standing choppable tree (when point/pointWater omitted)"),
  pointNearestFire: z.boolean().optional().describe("point at the nearest live campfire (prefer own; when point/water/chop omitted)"),
}).describe("Tutorial coach: bag/item glow + world arrow (client-only)");

/** Optional pay when a step completes (server grants on advance). */
export const QuestStepRewardSchema = z.strictObject({
  obol: z.number().int().positive().optional().describe("wallet credit when this step completes"),
});

export const QuestStepSchema = z.strictObject({
  id: z.string().min(1),
  desc: z.string().min(1),
  journal: z.string().min(1),
  objective: QuestObjectiveSchema,
  coach: QuestCoachSchema.optional(),
  reward: QuestStepRewardSchema.optional().describe("granted once when this step completes (e.g. deliver pay)"),
});

export const QuestSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  title: z.string().min(1),
  summary: z.string().min(1),
  steps: z.array(QuestStepSchema).min(1),
});

export type QuestDoc = z.infer<typeof QuestSchema>;

// Models ------------------------------------------------------------------------
// Mirrors ModelDoc in src/model/parts.ts: archetype + palette + body + parts.

const vec3 = z.tuple([z.number(), z.number(), z.number()]);

/** Palette entry: "#rrggbb" or { base, shade } (base = another key or raw hex). */
const paletteEntry = z.union([
  z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb"),
  z.strictObject({ base: z.string(), shade: z.number().min(-1).max(1) }),
]);

const partRepeat = z
  .strictObject({
    count: z.int().min(2),
    step: vec3.optional(),
    ring: z.strictObject({ r: z.number() }).optional(),
  })
  .refine((r) => !!r.step !== !!r.ring, { message: "repeat needs exactly one of step / ring" });

const partBase = {
  color: z.string().min(1),
  at: vec3,
  /** Euler XYZ radians — leans box/blob/cone/cylinder off-axis (bow limbs). */
  rot: vec3.optional(),
  anchor: z.string().optional(),
  /** Emit inside this named limb group so the part rides the joint (a jointed
   *  quadruped's "neck"/"tail"). Root emission when the build lacks the limb. */
  limb: z.string().optional(),
  mirror: z.boolean().optional(),
  repeat: partRepeat.optional(),
};

/** Shared modifiers for prims that place at a point (not rod). */
const partMods = {
  anchor: z.string().optional(),
  limb: z.string().optional(),
  mirror: z.boolean().optional(),
  repeat: partRepeat.optional(),
};

export const PartSchema = z.discriminatedUnion("prim", [
  z.strictObject({ prim: z.literal("box"), size: vec3, ...partBase }),
  z.strictObject({ prim: z.literal("blob"), r: z.number().positive(), ...partBase }),
  z.strictObject({ prim: z.literal("cone"), r: z.number().positive(), h: z.number().positive(), ...partBase }),
  z.strictObject({
    prim: z.literal("cylinder"),
    rTop: z.number().nonnegative(), rBottom: z.number().nonnegative(), h: z.number().positive(),
    ...partBase,
  }),
  z.strictObject({
    prim: z.literal("tube"),
    r: z.number().positive(), len: z.number().positive(), axis: z.enum(["x", "z"]).optional(),
    ...partBase,
  }),
  // Point-to-point cylinder — no `at`/`rot` (orientation from from→to).
  z.strictObject({
    prim: z.literal("rod"),
    from: vec3,
    to: vec3,
    r: z.number().positive(),
    color: z.string().min(1),
    ...partMods,
  }),
  // ---- Rounded prims (plan 28 — smooth toon ramp, organic masses) ----
  // Smooth sphere of flesh. depth ovalizes local z (compose with rot to pick
  // the squash axis — scale applies before rotation).
  z.strictObject({
    prim: z.literal("ball"),
    r: z.number().positive(),
    depth: z.number().positive().optional(),
    ...partBase,
  }),
  // Partial torus curving UP at the ends; `at` anchors the LOW point. rot.z = π
  // flips smile → frown. sweep in radians.
  z.strictObject({
    prim: z.literal("arc"),
    r: z.number().positive(),
    tube: z.number().positive(),
    sweep: z.number().positive().max(Math.PI * 2),
    ...partBase,
  }),
  // Revolve a silhouette around a vertical axis. at = [x, baseY, z]; profile =
  // [radius, y] pairs bottom→top, y RELATIVE to baseY (radius 0 closes a pole).
  // depth ovalizes/stretches z; frontBulge = [y, dz] pairs pushing only the
  // +z face forward.
  z.strictObject({
    prim: z.literal("lathe"),
    profile: z.array(z.tuple([z.number().nonnegative(), z.number()])).min(2),
    depth: z.number().positive().optional(),
    segs: z.int().min(3).max(24).optional(),
    frontBulge: z.array(z.tuple([z.number(), z.number()])).optional(),
    ...partBase,
  }),
  // Tapered capsule between two points — from/to like rod (mirror flips both
  // ends' x). Caps sit centered ON the endpoints (mesh extends r0/r1 past —
  // bury caps in adjoining masses). depth scales local z; bulge = muscle belly.
  z.strictObject({
    prim: z.literal("bone"),
    from: vec3,
    to: vec3,
    r0: z.number().positive(),
    r1: z.number().positive(),
    depth: z.number().positive().optional(),
    bulge: z.strictObject({ amount: z.number(), at: z.number().min(0).max(1).optional() }).optional(),
    color: z.string().min(1),
    ...partMods,
  }),
]);

/** Quadruped body params (colors are palette keys / raw hex). */
const quadBody = z.strictObject({
  bodyColor: z.string(),
  legColor: z.string().optional(),
  headColor: z.string().optional(),
  hoofColor: z.string().optional(),
  length: z.number().positive().optional(),
  width: z.number().positive().optional(),
  thickness: z.number().positive().optional(),
  legHeight: z.number().positive().optional(),
  legRadius: z.number().positive().optional(),
  headSize: z.number().positive().optional(),
  neck: z.number().optional(),
  /** Extra head height (world units) — an arched, raised neck (horse). */
  neckRaise: z.number().optional(),
  /** Neck+head inside a `neck` limb the gait nods; parts ride it via `limb`. */
  neckJoint: z.boolean().optional(),
  /** Empty `tail` limb pivot at the rump for doc tail parts the gait sways. */
  tailJoint: z.boolean().optional(),
});

/** Humanoid body params (see src/model/humanoid.ts) — the rounded, fully-
 *  jointed biped (knees/elbows/torso/head pivots, richer gait + attack clips). */
const humanoidBody = z.strictObject({
  skin: z.string(),
  scale: z.number().positive().optional(),
  /** Body-type frame ("male"/"female") — female = slimmer limbs, rounder hips,
   *  waist pinch + bust. Default male. */
  sex: z.enum(SEXES).optional(),
  /** Named silhouette preset (average/stout/slight/squat) — girth/width/leg-
   *  length multipliers over the shared frame. Default average. */
  build: z.enum(BODY_BUILDS).optional(),
  lower: z.discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("legs"), color: z.string(), boot: z.string().optional(),
      /** Shorts: the shin renders in skin instead of the trouser shade. */
      short: z.boolean().optional(),
    }),
    z.strictObject({ kind: z.literal("skirt"), color: z.string(), hem: z.string().optional() }),
  ]),
  torso: z.strictObject({
    color: z.string(),
    width: z.number().positive().optional(),
    height: z.number().positive().optional(),
    depth: z.number().positive().optional(),
  }),
  arms: z
    .strictObject({ sleeve: z.string().optional(), length: z.number().positive().optional() })
    .optional(),
  head: z
    .strictObject({
      hair: z.string().optional(),
      hairStyle: z.enum(HAIR_STYLES).optional(),
      /** Optional beard color (palette key or hex). Defaults to hair color when
       *  a beardStyle is set. Use for gray beards on dark hair (dekkar). */
      beard: z.string().optional(),
      /** Built-in beard build (BEARD_STYLES). Prefer over face-add beard parts. */
      beardStyle: z.enum(BEARD_STYLES).optional(),
      eye: z.string().optional(),
      /** When false, skip the default eyes/brows/mouth so a slots.face part
       *  can own the whole face (goblin-face). Default true. Built-in beards
       *  leave this unset — they only add jaw/chin mass over the default face. */
      features: z.boolean().optional(),
      /** Facial expression pose (default "happy"; "talking" animates the mouth
       *  open/closed). Geometry per expression: src/model/face.ts. */
      expression: z.enum(FACE_EXPRESSIONS).optional(),
    })
    .optional(),
});

/** Bird body params (see src/model/bird.ts). */
const birdBody = z.strictObject({
  bodyColor: z.string(),
  headColor: z.string().optional(),
  wingColor: z.string().optional(),
  tailColor: z.string().optional(),
  legColor: z.string().optional(),
  beakColor: z.string().optional(),
  length: z.number().positive().optional(),
  width: z.number().positive().optional(),
  thickness: z.number().positive().optional(),
  legHeight: z.number().positive().optional(),
  headSize: z.number().positive().optional(),
  neck: z.number().optional(),
  tailLift: z.number().optional(),
});

/** Bug body params (see src/model/bug.ts). */
const bugBody = z.strictObject({
  bodyColor: z.string(),
  headColor: z.string().optional(),
  legColor: z.string().optional(),
  abdomenColor: z.string().optional(),
  legPairs: z.int().min(1).max(8).optional(),
  length: z.number().positive().optional(),
  width: z.number().positive().optional(),
  thickness: z.number().positive().optional(),
  stance: z.number().positive().optional(),
  legSpread: z.number().positive().optional(),
  headSize: z.number().positive().optional(),
  abdomen: z.number().nonnegative().optional(),
});

/** Archetype → body-params schema ("parts" has no body). */
const BODY_SCHEMAS = { quadruped: quadBody, humanoid: humanoidBody, bird: birdBody, bug: bugBody } as const;

/** Per-instance seeded variation (deterministic from the spawn id — same on
 *  every client, the server, and the studio). All knobs are VISUAL only: the
 *  NPC spec's collider/nameplate heights are untouched. */
const variationSchema = z.strictObject({
  /** ± palette lightness jitter applied uniformly post-resolve. */
  shade: z.number().min(0).max(0.25).optional().describe("± palette lightness jitter per instance (keep ≤ 0.2; ~0.07 reads natural)"),
  /** ± uniform build-scale jitter (scales the emitted geometry, NOT the group —
   *  combat's flinch pop owns group.scale). */
  scale: z.number().min(0).max(0.12).optional().describe("± build-scale jitter per instance (keep ≤ 0.05 — colliders/nameplates don't scale)"),
  /** Randomize each instance's stride phase so a crowd doesn't march in step. */
  gaitPhase: z.boolean().optional().describe("randomize stride phase so a crowd doesn't march in step"),
  /** ± stride-cadence jitter (fraction). */
  gaitSpeed: z.number().min(0).max(0.3).optional().describe("± stride-cadence jitter fraction"),
}).describe("Seeded PER-SPAWN jitter, deterministic from the spawn id (same on every client/server/studio). Visual only.");

/** Named points on a held model in LOCAL model space. `grip` is the seat —
 *  item `hold.hilt` must equal it; secondary points (string/tip/head/guard) drive
 *  inspect-model dumps + hold-seating validation + dual-hand pose goals. */
export const ModelLandmarksSchema = z
  .strictObject({
    grip: vec3
      .optional()
      .describe("handle/grip center — THE seat point; item hold.hilt must match exactly"),
    string: vec3.optional().describe("bowstring midpoint (bows)"),
    tip: vec3.optional().describe("blade tip / rod tip / point (melee, rods, lances)"),
    head: vec3.optional().describe("weapon head (axe bit, mace, …)"),
    guard: vec3
      .optional()
      .describe(
        "crossguard / hand-stop along the shaft (greatswords) — dual-hand pose scoring uses this so the off fist stays on the hilt below the bar, not on it",
      ),
  })
  .describe("Local-space landmarks for held props. grip is required on any model referenced by a weapon/shield/rod hold");

export const ModelSchema = z
  .strictObject({
    $schema: z.string().optional(),
    id: idField,
    archetype: z.enum(["parts", "quadruped", "humanoid", "bird", "bug"]),
    /** VARIANT: merge over this base model doc (same archetype, single-level —
     *  the base may not itself have a base). palette shallow-merges, body
     *  deep-merges, parts APPEND, slots shallow-merge, landmarks shallow-merge. */
    base: idField.optional().describe("VARIANT: merge over this base model (same archetype, single level). palette shallow-merges, body deep-merges, parts APPEND, slots merge, landmarks shallow-merge"),
    palette: z.record(z.string(), paletteEntry).optional(),
    body: z.record(z.string(), z.unknown()).optional(),
    parts: z.array(PartSchema).optional(),
    /** Slot fillers: slot name (ARCHETYPE_SLOTS) → a parts-library doc id
     *  (anchor slots) or an item type (socket slots: held/offhand). */
    slots: z.record(z.string(), z.string()).optional()
      .describe("slot → filler. Anchor slots (humanoid: headwear/face/back/waist; quadruped: headwear/saddle; bird: headwear/back; bug: back) take a content/parts doc id; held/offhand take an item type WITH a weapon block (or held-tool fishing/chopping for held)"),
    variation: variationSchema.optional(),
    /** Grip/seat + secondary points for held models (weapons, shields, rods). */
    landmarks: ModelLandmarksSchema.optional(),
  })
  .superRefine((doc, ctx) => {
    // Body shape is archetype-specific. Variant docs (base set) may carry a
    // PARTIAL body — required fields come from the base, checked by validate.ts
    // against the merged doc.
    if (doc.archetype === "parts") {
      if (doc.body !== undefined)
        ctx.addIssue({ code: "custom", path: ["body"], message: `"parts" models take no body — put everything in parts[]` });
      if (!doc.parts?.length && !doc.base)
        ctx.addIssue({ code: "custom", path: ["parts"], message: `"parts" models need a non-empty parts[]` });
      if (doc.slots !== undefined)
        ctx.addIssue({ code: "custom", path: ["slots"], message: `"parts" models have no archetype slots` });
    } else if (!doc.base) {
      const res = BODY_SCHEMAS[doc.archetype].safeParse(doc.body ?? {});
      if (!res.success) {
        for (const i of res.error.issues) {
          ctx.addIssue({ code: "custom", path: ["body", ...i.path.map(String)], message: i.message });
        }
      }
    }
  });

export type ModelDocParsed = z.infer<typeof ModelSchema>;

// Parts library (content/parts/*.json) ------------------------------------------
// Anchored part GROUPS that fill archetype slots (helmets, hats, packs). The
// `parts` use the exact model-part vocabulary; `at` is an offset from the slot's
// anchor (no `anchor` field — the slot decides where it goes, and on jointed
// rigs the group is emitted INSIDE the host limb so it rides the animation).

export const PartGroupSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  /** Archetypes whose slots may host this group. */
  fits: z.array(z.enum(["quadruped", "humanoid", "bird", "bug"])).min(1)
    .describe("archetypes whose slots may host this group (validated at the referencing model)"),
  /** Face-slot contract (humanoid `slots.face` only):
   *  - `face-replace` — owns the whole face (goblin-face). Build skips the
   *    default humanoid eyes/brows/mouth (`body.head.features: false`).
   *  - `face-add` — accessory over the default face (beards). Default when
   *    omitted on a face filler. Headwear/packs ignore this field. */
  role: z.enum(["face-replace", "face-add"]).optional()
    .describe("face slot only: face-replace skips default humanoid face features; face-add keeps them (beards)"),
  /** Headwear contract (humanoid `slots.headwear` / wear.slot head):
   *  when true or omitted, the host's hair is suppressed (`hairStyle: "none"`)
   *  so helmets/caps don't clip through a cap crop. Crowns and circlets that
   *  sit ON hair set `hidesHair: false` (see gold-crown). Face fillers and
   *  packs ignore this field. */
  hidesHair: z.boolean().optional()
    .describe("headwear only: omit/true = hide host hair (helmets); false = keep hair under (crowns)"),
  palette: z.record(z.string(), paletteEntry).optional(),
  parts: z.array(PartSchema).min(1)
    .describe("normal Part vocabulary, but `at` is relative to the SLOT's anchor and `anchor` is not allowed"),
}).describe("A parts-library piece (helmet/hat/pack) that fills a model's anchor slot — rides the host joint on jointed rigs");

export type PartGroupDoc = z.infer<typeof PartGroupSchema>;

// Items -------------------------------------------------------------------------
// Mirrors ItemSpec/WeaponSpec in src/items/define.ts; `model` refs a model doc.

const holdSchema = z.strictObject({
  hilt: vec3.describe(
    "MUST equal the model landmarks.grip — the handle center in model local space. seatWeapon pins this point to the hand socket origin for any rot",
  ),
  rot: vec3.describe(
    "BASE orientation in the fist (socket-relative euler; +y up, +z forward). This is the ONE source of truth for how the mesh sits — item hold, not clip grip. Clip/stance grip is a small DELTA only",
  ),
  scale: z.number().positive().optional(),
  offset: vec3
    .optional()
    .describe(
      "tiny styling nudge AFTER hilt→socket alignment. Prefer 0; large values pull the real grip off the fist (validate warns above ~0.08, still allows 2H diagonal carry)",
    ),
}).describe(
  "How the item seats in the fist. Grip POINT is model landmarks.grip (= hilt); orientation is this rot. Tune rot in studio; do not re-seat with grip deltas",
);

const rangeBand = z.strictObject({ max: z.number().positive(), hit: z.number().min(0).max(1) });

const rangedSchema = z
  .strictObject({
    ammo: z.string().min(1).describe("item type consumed per shot (e.g. wooden-arrow)"),
    bands: z.array(rangeBand).min(1).describe("distance→hit%% bands, ascending by max; the LAST max should equal attackRange"),
    projectileSpeed: z.number().positive().describe("cosmetic arrow speed, u/s"),
  })
  .refine((r) => r.bands.every((b, i) => i === 0 || b.max > r.bands[i - 1]!.max), {
    path: ["bands"],
    message: "bands must ascend by max",
  });

export const WeaponSchema = z.strictObject({
  archetype: z.enum(WEAPON_ARCHETYPE_IDS)
    .describe("animation archetype — attack/carry/ready/hold-override shared by every weapon of this kind (content/anim/clips.json archetypes.<id>). Per-item hold seats THIS mesh"),
  damage: z.number().positive(),
  attackCooldown: z.number().positive().describe("seconds between swings (unarmed punch is 0.6)"),
  staminaCost: z.number().nonnegative(),
  attackRange: z.number().positive().describe("melee reach / ranged hard cap, world units (punch is 2.2)"),
  grip: z.enum(["one-hand", "two-hand"]).describe("combat mechanics: two-hand widens the cleave arc + locks offhand; animation comes from archetype"),
  style: z.enum(["swing", "chop", "thrust", "draw"]).describe("combat mechanics: light-attack cleave arc (STYLE_ARC) / draw = ranged; animation comes from archetype"),
  ranged: rangedSchema.optional().describe("presence = fires projectiles (bows)"),
  requiresBox: z.string().optional().describe("skill box id that must be unlocked to equip this weapon type (Plan 09 weapon-type gate)"),
  moves: z.array(z.string().min(1)).optional().describe("special-move ids (content/moves) this weapon supports — ability slots 1–4 (plan 14; combat strip is F primary + four ability boxes). Primary/light attack is the weapon block itself (F)"),
  hold: holdSchema,
}).describe("Presence = EQUIPPABLE. The server reads these stats directly — they are authoritative");

export const WearSchema = z.strictObject({
  slot: z.enum(["head", "torso", "legs", "feet", "back"])
    .describe("body area this occupies — one worn item per slot"),
  armor: z.int().min(0).max(30)
    .describe("%% damage reduction while worn — summed across all worn gear, capped at 70%% server-side"),
  /** Attached geometry: a content/parts doc worn at the slot's anchor(s) —
   *  head/back at the existing headwear/back slots, torso/legs/feet at the
   *  torsowear/legwear/footwear slots (legs/feet fill both sides). */
  part: z.string().min(1).optional()
    .describe("content/parts doc id worn at the slot's anchor(s) — armor/hats/packs; doubles as the ground-drop model when `model` is omitted"),
  /** Coded wearable id (src/wear) — custom Three.js geometry for garments the
   *  parts prim language can't express (open-front cloaks, …). Mutually
   *  exclusive with `part` for geometry; still seat via attachCodedWear. */
  coded: z.string().min(1).optional()
    .describe("coded wearable builder id (src/wear CODED_WEAR) — freeform geometry; ground drop via the same registry when model is omitted"),
  /** Cloth: palette-key overrides on the body while worn (shirt/trousers/boot). */
  recolor: z.record(z.string(), z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb")).optional()
    .describe("palette-key → color overrides applied to the body while worn (cloth: shirt/trousers/boot)"),
  /** Body-param overrides deep-merged while worn (e.g. arms.sleeve:\"skin\" for
   *  short sleeves, lower.short:true for shorts). */
  body: z.record(z.string(), z.unknown()).optional()
    .describe("humanoid body params deep-merged while worn (e.g. {arms:{sleeve:\"skin\"}} = short sleeves)"),
})
  .refine((w) => w.part || w.coded || w.recolor || w.body, {
    message: "a wear block needs at least one of part / coded / recolor / body",
  })
  .refine((w) => !(w.part && w.coded), {
    message: "wear.part and wear.coded are mutually exclusive — pick prim parts or coded geometry",
  })
  .describe("Presence = WEARABLE on a humanoid (the hero's paperdoll). Same data-is-the-switch pattern as `weapon`");

export const ShieldSchema = z.strictObject({
  block: z.number().min(0).max(100).describe("%% of FRONTAL damage soaked while the block is held up"),
  staminaPerHit: z.number().nonnegative().describe("stamina drained per blocked hit — running dry staggers the blocker open"),
  requiresBox: z.string().optional().describe("skill box id that must be unlocked to equip it (plan-09 gate)"),
  hold: holdSchema,
}).describe("Presence = SHIELD (plan 14): equips to the OFF hand, only alongside a one-hand weapon (a two-hander locks the offhand). Hold-to-block soaks frontal damage");

export const FishingSchema = z.strictObject({
  castRange: z.number().positive().optional().describe("max reach to a valid water cast point, world units (default CAST_RANGE 12)"),
  attractMul: z.number().positive().optional().describe("× the fish notice roll — a better rod draws fish to the bobber sooner (default 1)"),
  requiresBox: z.string().optional().describe("skill box id that must be unlocked to equip this rod tier (plan-09 gate)"),
  hold: holdSchema,
}).describe("Presence = FISHING ROD (plan 10): equippable to the held-tool channel (main hand, NetPlayer.tool) and enables casting. Same block-presence-is-the-switch pattern as `weapon`");

export const ChoppingSchema = z.strictObject({
  requiresBox: z.string().optional().describe("skill box id that must be unlocked to equip this hatchet tier (future Woodcutting gate)"),
  hold: holdSchema,
}).describe("Presence = HATCHET (tree-cutting tool): equippable to the held-tool channel (main hand, NetPlayer.tool) and enables cutting trees. Same block-presence-is-the-switch pattern as `fishing`");

export const NetItemSchema = z.strictObject({
  soakTime: z.number().positive().optional().describe("seconds the deployed net soaks before it's ready (default SOAK_TIME 540; box soakMul multiplies it)"),
  tier: z.int().nonnegative().optional().describe("net tier — reserved for CP2 bulk-table tiers (default 0)"),
  requiresBox: z.string().optional().describe("skill box id that must be unlocked to deploy this net tier (plan-09 gate)"),
}).describe("Presence = DEPLOYABLE FISHING NET (plan 10): set at the water's edge, soaks on a durable timer, collected for a bulk haul. Not held — consumed/anchored on deploy, returned on collect");

// Loot tables (shared shape): NPC drop tables, prop/chest loot, and a trap's
// haul all roll through src/loot.ts rollLoot. Declared here (above ItemSchema)
// because the trap block embeds one.
export const LootSchema = z.array(
  z.strictObject({
    type: z.string().min(1).describe("item type (content/items)"),
    chance: z.number().gt(0).max(1).describe("drop probability rolled on defeat"),
    min: z.int().positive().optional().describe("min units dropped (default 1)"),
    max: z.int().positive().optional(),
    trophy: z.boolean().optional().describe("plan 11: a rare-trophy entry — a hunting-bow take scales its chance by the stalker's trophy tier (sim trophyChance); any other kill rolls the base odds"),
  }),
).describe("Rolled on defeat — needs a combat block to ever drop");

export const HuntingItemSchema = z.strictObject({
  aimHitBonus: z.number().min(0).max(1).optional().describe("flat + to the rolled distance-band hit%% while stalking (folds with the Stalking box grants, capped at AIM_HIT_CAP 0.95)"),
  trophyTier: z.int().nonnegative().optional().describe("+ trophy-tier access granted by the bow itself (sums with box grants — CP2 bow tiers)"),
  requiresBox: z.string().optional().describe("skill box id that must be unlocked to equip this bow tier (plan-09 gate)"),
}).describe("Presence = HUNTING BOW marker (plan 11): requires weapon.ranged. Routes prey kills to Hunting/stalking XP (tool-gated at the kill site), raises the over-the-shoulder aim camera while drawn, and folds its bonuses with the Stalking boxes");

export const TrapItemSchema = z.strictObject({
  soakTime: z.number().positive().optional().describe("seconds the deployed trap soaks before it's ready (default TRAP_SOAK 480; box soakMul multiplies it)"),
  capacityMin: z.int().positive().optional().describe("min small game per haul (default TRAP_CAPACITY_MIN 2; uniform roll)"),
  capacityMax: z.int().positive().optional().describe("max small game per haul (default TRAP_CAPACITY_MAX 4)"),
  tier: z.int().nonnegative().optional().describe("trap tier — reserved for CP2 haul-table tiers (default 0)"),
  requiresBox: z.string().optional().describe("skill box id that must be unlocked to deploy this trap tier (plan-09 gate)"),
  haul: LootSchema.describe("the per-small-game roll, self-contained on the item — rolled capacity× per collect (src/loot.ts rollLoot; commons only — bulk is Trapping's identity)"),
}).describe("Presence = DEPLOYABLE TRAP (plan 11): set on huntable ground, soaks on a durable timer, collected for a bulk meat/hide haul. Not held — consumed/anchored on deploy, returned on collect");

export const BrewItemSchema = z.strictObject({
  soakTime: z.number().positive().optional().describe("seconds the deployed fermenter soaks before it's ready (default BREW_SOAK 420; box soakMul multiplies it)"),
  capacityMin: z.int().positive().optional().describe("min brewed goods per haul (default BREW_CAPACITY_MIN 3; uniform roll)"),
  capacityMax: z.int().positive().optional().describe("max brewed goods per haul (default BREW_CAPACITY_MAX 6)"),
  tier: z.int().nonnegative().optional().describe("fermenter tier — reserved for later haul-table tiers (default 0)"),
  requiresBox: z.string().optional().describe("skill box id that must be unlocked to deploy this fermenter tier (plan-09 gate)"),
  haul: LootSchema.describe("the per-bottle roll, self-contained on the item — rolled capacity× per collect (src/loot.ts rollLoot; bulk is Brewing's identity)"),
}).describe("Presence = DEPLOYABLE FERMENTER (plan 18 CP3, Brewing branch): set on dry ground, soaks on a durable timer, collected for a bulk brewed haul. Not held — anchored on deploy, returned on collect (Trapping's twin on the Cooking skill)");

export const FoodItemSchema = z.strictObject({
  heal: z.number().nonnegative().optional().describe("HP restored per unit eaten (server-authoritative; clamped to maxHp)"),
  stamina: z.number().nonnegative().optional().describe("stamina restored per unit eaten (client-predicted + server-gate bumped)"),
}).refine((f) => (f.heal ?? 0) > 0 || (f.stamina ?? 0) > 0, { message: "food must heal HP and/or restore stamina" })
  .describe("Presence = EDIBLE (plan 18): consuming one unit heals HP + restores stamina, then removes it from the bag. Cooked dishes carry this");

export const CookItemSchema = z.strictObject({
  into: z.string().min(1).describe("the cooked item type produced by a successful cook (a real item type — the edible dish)"),
}).describe("Presence = COOKABLE RAW (plan 18): cooking it at a hearth yields `into`. Cook time + XP live in content/tunables/cooking.json (keyed by the RAW type), the one home for that pacing");

export const FishItemSchema = z.strictObject({
  tier: z.int().nonnegative().describe("hook tier — this fish only takes the hook when the angler's folded tableTier (box grants) ≥ this"),
  size: z.number().positive().optional().describe("shadow/model scale multiplier (default 1) — proportional to the fish, not its rarity"),
  cruiseSpeed: z.number().positive().optional().describe("u/s while wandering its water (default sim CRUISE_SPEED 0.8)"),
  dartSpeed: z.number().positive().optional().describe("u/s burst when spooked (default sim DART_SPEED 2.4)"),
  curiosity: z.number().min(0).max(1).optional().describe("0 timid … 1 bold — the notice-roll base (default sim CURIOSITY 0.6; dynamic wariness scales it down)"),
  patience: z.number().positive().optional().describe("seconds of investigate budget base — how long it circles the bobber before deciding (default sim PATIENCE 5)"),
  nibblesMin: z.int().nonnegative().optional().describe("min nibble tugs before the bite (default tunables nibbleMin)"),
  nibblesMax: z.int().nonnegative().optional().describe("max nibble tugs before the bite (default tunables nibbleMax)"),
  fightMul: z.number().positive().optional().describe("reel-fight difficulty × — scales minigame arrow count AND pace (default 1)"),
}).describe("Presence = CATCHABLE FISH (Living Waters rework): lives in a map doc's waters[] population, swims as a visible shadow, runs the notice→approach→investigate→nibble→bite encounter, and IS the catch when landed. Composes with `cook` (raw fish are cookable)");

/** Combat-armor quality rung. Weight class (Light/Medium/Heavy) is the family;
 *  tier is a within-family upgrade (Basic → Trimmed → Elite). Not a weight class. */
export const ArmorTierSchema = z.enum(["basic", "trimmed", "elite"])
  .describe("Combat armor quality rung — basic (entry) / trimmed (mid) / elite (top). Omit on non-combat wearables.");

export const ItemSchema = z.strictObject({
  $schema: z.string().optional(),
  type: idField,
  name: z.string().min(1),
  icon: z.string().min(1),
  radius: z.number().positive(),
  /** Model doc id (content/models) or a coded-model id. Optional for wearables
   *  with a `wear.part` — the part doc doubles as the ground-drop model. */
  model: z.string().min(1).optional(),
  stack: z.int().positive().optional(),
  bundle: z.int().positive().optional(),
  /** Combat armor quality rung (basic/trimmed/elite). Tag combat wearables;
   *  omit on civilian cloth / cosmetics. */
  tier: ArmorTierSchema.optional(),
  weapon: WeaponSchema.optional(),
  wear: WearSchema.optional(),
  shield: ShieldSchema.optional(),
  fishing: FishingSchema.optional(),
  chopping: ChoppingSchema.optional(),
  net: NetItemSchema.optional(),
  hunting: HuntingItemSchema.optional(),
  trap: TrapItemSchema.optional(),
  brew: BrewItemSchema.optional(),
  food: FoodItemSchema.optional(),
  cook: CookItemSchema.optional(),
  fish: FishItemSchema.optional(),
}).superRefine((doc, ctx) => {
  if (!doc.model && !doc.wear?.part && !doc.wear?.coded) {
    ctx.addIssue({
      code: "custom",
      path: ["model"],
      message: "an item needs a model (or wear.part / wear.coded to double as one)",
    });
  }
  if (doc.weapon && doc.wear) {
    ctx.addIssue({ code: "custom", path: ["wear"], message: "an item can't be both a weapon and a wearable (hand vs body equip paths)" });
  }
  if (doc.shield && (doc.weapon || doc.wear)) {
    ctx.addIssue({ code: "custom", path: ["shield"], message: "a shield is its own equip path — it can't also be a weapon or a wearable" });
  }
  if (doc.fishing && (doc.weapon || doc.wear || doc.shield || doc.chopping)) {
    ctx.addIssue({ code: "custom", path: ["fishing"], message: "a rod is its own equip path (the held-tool channel) — it can't also be a weapon/wearable/shield/hatchet" });
  }
  if (doc.chopping && (doc.weapon || doc.wear || doc.shield || doc.fishing)) {
    ctx.addIssue({ code: "custom", path: ["chopping"], message: "a hatchet is its own equip path (the held-tool channel) — it can't also be a weapon/wearable/shield/rod" });
  }
  if (doc.net && (doc.weapon || doc.wear || doc.shield || doc.fishing || doc.chopping)) {
    ctx.addIssue({ code: "custom", path: ["net"], message: "a net is a deployable, not an equippable — it can't carry another equip block" });
  }
  if (doc.hunting && !doc.weapon?.ranged) {
    ctx.addIssue({ code: "custom", path: ["hunting"], message: "a hunting bow marker needs a weapon.ranged block — it rides the normal weapon equip + fire path" });
  }
  if (doc.trap && (doc.weapon || doc.wear || doc.shield || doc.fishing || doc.chopping || doc.net)) {
    ctx.addIssue({ code: "custom", path: ["trap"], message: "a trap is a deployable, not an equippable — it can't carry another equip block" });
  }
  if (doc.brew && (doc.weapon || doc.wear || doc.shield || doc.fishing || doc.chopping || doc.net || doc.trap)) {
    ctx.addIssue({ code: "custom", path: ["brew"], message: "a fermenter is a deployable, not an equippable — it can't carry another equip/deploy block" });
  }
  if (doc.food && (doc.weapon || doc.wear || doc.shield || doc.fishing || doc.chopping || doc.net || doc.trap || doc.brew)) {
    ctx.addIssue({ code: "custom", path: ["food"], message: "food is a consumable — it can't also be an equippable or deployable" });
  }
  if (doc.food && doc.cook) {
    ctx.addIssue({ code: "custom", path: ["cook"], message: "an item is either a cooked dish (food) or a raw ingredient (cook), not both" });
  }
  if (doc.fish && (doc.weapon || doc.wear || doc.shield || doc.fishing || doc.chopping || doc.net || doc.trap || doc.brew)) {
    ctx.addIssue({ code: "custom", path: ["fish"], message: "a fish is a catchable good — it can't carry an equip/deploy block (cook/food are fine)" });
  }
});

export type ItemDoc = z.infer<typeof ItemSchema>;

// Shops -------------------------------------------------------------------------
// Mirrors ShopSpec in src/shop/define.ts. An NPC-backed vendor: `npc` is the
// shopkeeper spawn id whose conversation lock gates buy/sell (cross-ref'd in
// validate.ts), `wares` its price list. Stock is infinite in v1 (no shared mutable
// stock — sidesteps multi-world sync); buy/sell are gold↔item transfers.

export const ShopSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  name: z.string().min(1),
  /** Shopkeeper NPC spawn id — the conversation lock that gates trading. */
  npc: z.string().min(1),
  wares: z
    .array(
      z.strictObject({
        type: z.string().min(1),
        buy: z.int().nonnegative(),  // obol to buy one
        sell: z.int().nonnegative(), // obol paid when the player sells one back
      }),
    )
    .min(1),
});

export type ShopDoc = z.infer<typeof ShopSchema>;

// NPCs -----------------------------------------------------------------------------
// Mirrors NpcSpec in src/npc/define.ts, with two content-shaped changes: `model`
// refs a model doc, and the doc is a pure TEMPLATE (a "kind") — placement lives
// in the owning map doc's `npcs` array (plan 21; multi-instance NPCs like
// goblins/wildlife are several map spawns of one template, with per-spawn
// overrides). Roam gains modes: box (absolute), radius (around the spawn),
// world (whole-map margin).

const roamTuning = {
  speed: z.number().positive().optional(),
  pauseMin: z.number().nonnegative().optional(),
  pauseMax: z.number().nonnegative().optional(),
};

export const RoamSchema = z.discriminatedUnion("mode", [
  z.strictObject({
    mode: z.literal("box"),
    minX: z.number(), maxX: z.number(), minZ: z.number(), maxZ: z.number(),
    ...roamTuning,
  }),
  z.strictObject({ mode: z.literal("radius"), r: z.number().positive(), ...roamTuning }),
  z.strictObject({ mode: z.literal("world"), ...roamTuning }),
]);

/** NPC ranged attack (plan 14 archers): the player-weapon `ranged` shape minus
 *  ammo — NPCs don't consume arrows. Bands roll hit chance by distance; the
 *  archer stands and shoots inside the last band, melee-falls-back when closed on. */
const npcRangedSchema = z
  .strictObject({
    bands: z.array(rangeBand).min(1).describe("distance→hit%% bands, ascending by max — the last max is the fire range"),
    projectileSpeed: z.number().positive().describe("cosmetic arrow speed, u/s"),
  })
  .refine((r) => r.bands.every((b, i) => i === 0 || b.max > r.bands[i - 1]!.max), {
    path: ["bands"],
    message: "bands must ascend by max",
  });

/** Shared combat crowd-control side effects (snare / stun / slow / knockback).
 *  Attached to moves and NPC combat attacks; applied on landed hits (melee or
 *  ranged pending-hit arrival). */
export const CombatEffectSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("snare"),
    duration: z.number().positive().describe("seconds rooted in place (can still act)"),
  }),
  z.strictObject({
    kind: z.literal("stun"),
    duration: z.number().positive().describe("seconds frozen (no move, no act)"),
  }),
  z.strictObject({
    kind: z.literal("slow"),
    duration: z.number().positive().describe("seconds at reduced move speed (can still act)"),
    mult: z.number().gt(0).lt(1).describe("speed multiplier while slowed (0.7 = 30% slow)"),
  }),
  z.strictObject({
    kind: z.literal("knockback"),
    distance: z.number().nonnegative().describe("horizontal shove distance, world units"),
    height: z.number().nonnegative().describe("vertical loft apex, world units (0 = ground slide)"),
  }),
]);

export const CombatSchema = z.strictObject({
  hp: z.number().positive(),
  stamina: z.number().positive(),
  aggressive: z.boolean().describe("hostile temperament (authoring/studio). Focus never starts a fight — unprovoked chase needs territory+hostile faction; any hit provokes retaliation"),
  attackRange: z.number().positive().optional().describe("melee reach in world units (~2.2 humanoid)"),
  attackDamage: z.number().positive().optional(),
  attackCooldown: z.number().positive().optional().describe("seconds between swings"),
  pursueSpeed: z.number().positive().optional().describe("chase speed, u/s (player walks ~4.2)"),
  respawn: z.number().positive().optional().describe("seconds until this NPC respawns after defeat (default RESPAWN_DELAY = 60). Variants deep-merge."),
  hpRegen: z.number().min(0).optional().describe("out-of-combat HP regen points/sec (default NPC_HP_REGEN = 1). 0 = never. Only while no combat target. Variants deep-merge."),
  ranged: npcRangedSchema.optional().describe("presence = ARCHER: holds range and fires projectiles, melee fallback when closed on (ammo-free)"),
  effects: z.array(CombatEffectSchema).optional().describe("crowd-control applied on each landed melee hit (snare/stun/knockback)"),
}).describe("Presence = FIGHTABLE (server-simulated). Omit the whole block for peaceful NPCs.");

export const TerritorySchema = z.strictObject({
  radius: z.number().positive().describe("hostiles inside this radius of the spawn point are attacked unprovoked"),
  leash: z.number().positive().describe("the chase gives up this far from home (walk back + heal). Keep > radius"),
}).describe("Presence = TERRITORIAL (auto-engages hostiles near its spawn). Distances from the spawn/home point.");

export const FleeSchema = z.strictObject({
  alertRadius: z.number().positive().optional().describe("player distance that spooks it"),
  runAlertMultiplier: z.number().positive().optional().describe("running players spook from alertRadius × this"),
  safeRadius: z.number().positive().optional().describe("calms beyond this — MUST exceed alertRadius (hysteresis)"),
  fleeSpeed: z.number().positive().optional().describe("bolt speed, u/s — should beat the player's run (~7.5)"),
  panicMin: z.number().nonnegative().optional().describe("minimum seconds committed to a bolt"),
  turnRate: z.number().positive().optional(),
}).describe("Presence = skittish WILDLIFE; merged over shared defaults; overrides combat AI (prey never fights back). Needs a roam block.");

// (LootSchema is declared above ItemSchema — the trap block embeds it.)

const TalkCondSchema = z.strictObject({
  quest: z.string().min(1),
  is: z.enum(["unstarted", "active", "completed"]),
  step: z.string().optional(),
  has: z.strictObject({
    types: z.array(z.string().min(1)).min(1).describe("item types — bag must hold count across ANY of these (OR)"),
    count: z.int().positive().optional().describe("units required across the type list (default 1)"),
  }).optional().describe("bag gate: hide the choice unless the player holds these items (deliver turn-ins)"),
});

const TalkActionSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("startQuest"), id: z.string().min(1) }),
  z.strictObject({ kind: z.literal("advanceQuest"), id: z.string().min(1) }),
  z.strictObject({ kind: z.literal("giveItem"), type: z.string().min(1) }),
  z.strictObject({ kind: z.literal("openShop"), id: z.string().min(1) }),
  z.strictObject({ kind: z.literal("playCinematic"), id: z.string().min(1).describe("cinematic doc id (content/cinematics) — plays after the talk ends") }),
  z.strictObject({ kind: z.literal("custom"), tag: z.string().min(1) }),
]);

const TalkChoiceSchema = z.strictObject({
  text: z.string().min(1).describe("the player's line"),
  to: z.string().optional().describe("next node id — omit to end the conversation"),
  action: TalkActionSchema.optional().describe("fires on pick: startQuest/advanceQuest/giveItem/openShop/playCinematic/custom"),
  when: TalkCondSchema.optional().describe("gate: only shown when the quest state matches (unstarted/active[+step]/completed)"),
});

const TalkNodeSchema = z.strictObject({
  id: z.string().min(1),
  npc: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
  choices: z.array(TalkChoiceSchema).min(1).optional(),
  /** ENTRY gate: when met, this node opens the conversation instead of `root`
   *  (first matching node in list order). Soft-landing check-ins use this. */
  when: TalkCondSchema.optional().describe(
    "entry gate: open on this node when the quest state matches (checked before root)",
  ),
});

export const TalkSchema = z.strictObject({
  range: z.number().positive().optional().describe("how close the player must be to start (default radius ?? 6)"),
  root: z.string().min(1).describe("starting node id"),
  nodes: z.array(TalkNodeSchema).min(1),
}).describe("Presence = TALKABLE (server-coordinated, exclusive). Proof conversations in the studio Dialogue view");

export const NpcSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  /** VARIANT: merge over this base NPC doc (single level — the base may not itself
   *  have a base). Scalars/arrays REPLACE, combat DEEP-merges. Lets a named
   *  special (e.g. a boss) reuse a base kind's model + stats and override only
   *  what differs — the NPC twin of a model `base`. */
  base: idField.optional().describe("VARIANT: merge over this base NPC (single level). Scalars/arrays replace, combat deep-merges"),
  /** Uniform visual size multiplier (geometry only, via scalePrims). Collider +
   *  nameplate height stay authored — bump colliderR/H/height to match. Default 1. */
  scale: z.number().positive().optional().describe("uniform visual size multiplier (geometry only — set collider/height to match)"),
  /** Inherited from `base` when omitted on a variant. */
  name: z.string().min(1).optional(),
  /** Model doc id (content/models) or a coded-model id. Inherited from `base`. */
  model: z.string().min(1).optional(),
  dialogue: z.array(z.string().min(1)).min(1).optional(),
  nameplate: z.boolean().optional(),
  radius: z.number().positive().optional(),
  height: z.number().positive().optional(),
  solid: z.boolean().optional(),
  colliderR: z.number().positive().optional(),
  colliderH: z.number().positive().optional(),
  roam: RoamSchema.optional(),
  // Stored PARTIAL so a `base` variant can override just one stat (e.g. hp) and
  // deep-merge the rest from its base. A base-less doc's block must still be
  // complete — enforced in the superRefine below (mirrors the model `body` path).
  combat: CombatSchema.partial().optional(),
  talk: TalkSchema.optional(),
  flee: FleeSchema.optional(),
  loot: LootSchema.optional(),
  /** Faction tag — must be declared in content/world/factions.json. `player` is
   *  the implicit player-side id (never tag an NPC with it). */
  faction: idField.optional().describe("faction id from content/world/factions.json — drives stances (who it fights/assists)"),
  territory: TerritorySchema.optional(),
  assistRadius: z.number().positive().optional().describe("how far it scans for embattled faction-mates to assist (defaults from the faction doc)"),
}).superRefine((doc, ctx) => {
  // A base-less doc is a complete NPC; a variant (base set) inherits the core
  // fields, so they're required here only when there's no base. Completeness of
  // the MERGED variant is re-checked in validate.ts (mirrors ModelSchema's body).
  if (doc.base) return;
  for (const f of ["name", "model", "dialogue"] as const) {
    if (doc[f] === undefined)
      ctx.addIssue({ code: "custom", path: [f], message: `"${f}" is required (only a variant with a "base" may omit it)` });
  }
  // combat is stored partial (for variant overrides); a base-less block must be
  // complete. This also fires on validate.ts's merged re-parse (base cleared).
  if (doc.combat) {
    for (const f of ["hp", "stamina", "aggressive"] as const) {
      if (doc.combat[f] === undefined)
        ctx.addIssue({ code: "custom", path: ["combat", f], message: `"${f}" is required` });
    }
  }
});

export type NpcDoc = z.infer<typeof NpcSchema>;

// Decos ---------------------------------------------------------------------------
// Scatter metadata (mirrors DecoSpec minus build); geometry is a model ref —
// today always a coded model (seeded/procedural), but a `parts` doc works too.

/** Presence = INTERACTABLE: the model is click-focusable and offers `verb` in
 *  action-bar slot 1 (src/interact.ts client-side, the `interact` handler in
 *  index.ts server-side). Same block-presence-is-the-switch pattern as
 *  combat/talk/weapon. Kind-specific fields are cross-checked in validate.ts
 *  (shake needs dropItem, pick needs item). */
export const InteractSchema = z.strictObject({
  /** Display name shown in the focus banner ("✦ Oak Tree"). */
  name: z.string().min(1),
  /** Action-bar slot label ("Shake" / "Pick" / "Open" / "Read"). */
  verb: z.string().min(1),
  kind: z.enum(["open", "shake", "pick", "read", "use", "chop"]).describe(
    "open = one-time loot chest; shake = chance drop with per-instance cooldown; pick = item to bag, instance respawns; read = client-only plaque; use = client-only station verb (the range's Cook); chop = tree-cutting session (hatchet + minigame → logs + stump until respawn)",
  ),
  /** Reach (xz units) on top of the model's radius·scale. Default 2.2. */
  range: z.number().positive().optional(),
  /** shake: item type dropped on a successful roll (required for kind "shake"). */
  dropItem: z.string().min(1).optional(),
  /** shake: probability [0..1] a shake drops the item. Default 0.5. */
  dropChance: z.number().min(0).max(1).optional(),
  /** shake: seconds after a SUCCESSFUL drop before this instance can drop again. Default 20. */
  cooldown: z.number().nonnegative().optional(),
  /** pick: item type added to the picker's bag (required for kind "pick"). */
  item: z.string().min(1).optional(),
  /** pick/chop: seconds until the instance reappears (pick = flower; chop = full tree regrows from stump). Default 120 / 300. */
  respawn: z.number().positive().optional(),
  /** chop: log item type granted on a successful cut (required for kind "chop"). */
  logs: z.string().min(1).optional(),
  /** chop: how many log units granted on success. Default 1 (raise for larger trees). */
  logCount: z.int().positive().optional(),
  /** open: loot spawned on the ground when first opened (obol count = one pile). */
  loot: z
    .array(z.strictObject({ type: z.string().min(1), count: z.int().positive().optional() }))
    .min(1)
    .optional(),
});

export type InteractDoc = z.infer<typeof InteractSchema>;

export const DecoSchema = z.strictObject({
  $schema: z.string().optional(),
  type: idField,
  model: z.string().min(1),
  /** Random-spawn weight. 0 = placed landmark, never scattered. */
  weight: z.number().nonnegative(),
  mapColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb"),
  radius: z.number().positive(),
  solid: z.boolean().optional(),
  /** Collider height (world units, pre-scale — gravity plan). Bounds the solid
   *  vertically so a jump can clear/land on it; OMITTED = full-height blocking
   *  (trees keep their infinite trunk cylinder). Only meaningful with `solid`. */
  height: z.number().positive().optional(),
  /** With `height`: the top is a standable platform — jump onto the rock/crate.
   *  Needs `height` (an unbounded solid has no top). */
  standable: z.boolean().optional(),
  sway: z.number().nonnegative().optional(),
  habitat: z.enum(["land", "shore", "water"]).optional(),
  interact: InteractSchema.optional(),
});

export type DecoDoc = z.infer<typeof DecoSchema>;

// World roads (embedded in a map doc's `roads` — plan 20) ----------------------------

/** Road visual styles — one archetype (drape / verge / boost), two looks.
 *  Omit on a road entry → city (flagstone cobbles). */
export const ROAD_STYLES = ["city", "dirt"] as const;
export type RoadStyle = (typeof ROAD_STYLES)[number];

export const WorldRoadsSchema = z.array(
  z.strictObject({
    waypoints: z.array(z.tuple([z.number(), z.number()])).min(2),
    width: z.number().positive().optional(),
    /** Visual look only — `city` (default, flagstone cobbles) or `dirt` (terrain vertex paint + sparse pebbles). */
    style: z.enum(ROAD_STYLES).optional(),
  }),
);

// World structure placements (embedded in a map doc's `structures` — plan 20) --------

export const WorldStructuresSchema = z.array(
  z.strictObject({
    /** Structure spec id — coded `castle` (src/structure/index.ts) or a
     *  content/structures doc id. Cross-ref validated in validate.ts. */
    spec: z.string().min(1),
    /** Which named VARIATION of the style doc to build (a key in the doc's
     *  `variants`). Omitted → one is seed-picked from the doc's variants by
     *  position, so a cluster of placements looks varied for free. Ignored by
     *  the coded castle. */
    variant: z.string().min(1).optional(),
    /** Numeric overrides of a structure DOC's declared `params` (validate.ts
     *  cross-checks keys against the doc; applied ON TOP of the chosen
     *  variation's params). Not valid on "castle". */
    params: z.record(z.string(), z.number()).optional(),
    x: z.number(),
    z: z.number(),
    turns: z.int().min(0).max(3).optional(),
    baseY: z.number().optional(),
    footprint: z.number().positive().optional(),
    flatten: z
      .strictObject({
        // 0 is meaningful: r:0 + feather:0 fully DISABLES the pad (the blend
        // zone leaves the height untouched) — required for a structure built
        // OVER water (a pier/bridge), whose flatten pad would otherwise drain
        // the river. A normal building omits `flatten` and gets an auto pad.
        r: z.number().nonnegative().optional(),
        feather: z.number().nonnegative().optional(),
        y: z.number().optional(),
      })
      .optional(),
  }),
);

// Structures (content/structures/*.json) --------------------------------------------
// Data-driven BUILDINGS: an op list interpreted through the exact same
// StructureBuildCtx helpers coded buildings use (src/structure/doc.ts), so
// collision, walkable floors, cutaway tagging, and consolidation come free.
// Geometry is authored in local coords (feet at y=0, +z = south); colors are
// palette keys / #rrggbb. Docs may declare named numeric `params` and author
// any numeric op field as an arithmetic EXPRESSION over them ("hw - wallT") —
// resolved by src/structure/params.ts before interpretation, with per-placement
// overrides from world/structures.json.

const colorField = z.string().min(1);

/** A numeric op field that may instead be a param expression ("hw/2 - 0.5").
 *  TYPED as number: resolveStructureDoc (src/structure/params.ts) replaces
 *  every expression before any TS consumer sees the doc — interpretStructure
 *  resolves at entry, and re-parses so `inner`'s constraints judge the
 *  EVALUATED value. */
const num = (inner: z.ZodType<number> = z.number()): z.ZodType<number> =>
  z.union([
    inner,
    z.string().min(1).describe("arithmetic expression over the doc's params, e.g. \"hw - wallT\""),
  ]) as unknown as z.ZodType<number>;

/** Boolean guard spread into every op: when present and it evaluates FALSE
 *  against the resolved params, the op is dropped before interpretation. This is
 *  how one style doc expresses its variations — a "2-storey" variant sets
 *  `stories:2` and the upper-floor ops carry `when:"stories >= 2"`. Grammar:
 *  arithmetic over params + comparisons (< <= > >= == !=) + && || ! (params.ts
 *  `evalBool`); a bare number is truthy iff non-zero. */
const whenGuard = {
  when: z.string().min(1).optional().describe("boolean guard over params, e.g. \"stories >= 2\" — op dropped when false"),
};

/** One opening in a `room` wall: a walk-through door or a framed window.
 *  `at` = the opening's center along the wall's run axis (local coords). */
const roomOpening = z.strictObject({
  kind: z.enum(["door", "window"]),
  side: z.enum(["north", "south", "east", "west"]), // north = −z, west = −x
  at: num(),
  w: num(z.number().positive()).optional(),   // door/window width
  h: num(z.number().positive()).optional(),   // door height / window height
  cy: num().optional(),                       // window center height (default 1.8)
  ...whenGuard, // e.g. upper-storey windows: "stories >= 2"
});

const structureOp = z.discriminatedUnion("op", [
  /**
   * MASS — the primary architectural volume (think like an architect).
   * One continuous rectangular shell: overlapping full-face walls (sharp-edged
   * so flanks+lintels coplanar-join as a single plane), floor, optional ceiling,
   * openings punched into the mass, optional cornice crown under the roof.
   * Compose: base → mass → roof → chimney. Prefer this over stitching `room`s
   * for free-standing houses.
   */
  z.strictObject({
    op: z.literal("mass"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    baseY: num().describe("floor height of this storey/volume"),
    wallH: num(z.number().positive()),
    wallT: num(z.number().positive()),
    wallColor: colorField,
    floorColor: colorField,
    floorThickness: num(z.number().positive()).optional(),
    /** Ceiling slab at baseY+wallH. Default TRUE when omitted (roofed mass). Set false for open-top. */
    ceiling: z.boolean().optional().describe("default true — omit or true for a lid; false for open-top"),
    ceilingColor: colorField.optional(),
    /** Wall-top cornice under eaves. Default TRUE when omitted. Set false to skip. */
    cornice: z.boolean().optional().describe("default true — continuous wall-top crown under the roof"),
    corniceColor: colorField.optional(),
    corniceH: num(z.number().positive()).optional(),
    /** "shell" (default) = continuous sharp mass; "logs" = log-cabin ribs/posts. */
    finish: z.enum(["shell", "logs"]).optional(),
    logColor: colorField.optional(),
    plankFloor: z.boolean().optional(),
    plankWidth: num(z.number().positive()).optional(),
    /** Frame color for door openings (default dark oak). */
    frameColor: colorField.optional(),
    openings: z.array(roomOpening).optional(),
    /**
     * Concept-B timber package: wall-plate beams under eaves, corner posts,
     * diagonal braces, heavy door surround + leaf, heavy window frames.
     * Pure decoration (shell-tagged per face). Default off (BC).
     */
    timber: z.boolean().optional().describe("storybook timber trim package (plates/posts/braces/heavy door)"),
    /** Timber color (defaults to frameColor). */
    timberColor: colorField.optional(),
    ...whenGuard,
  }).describe("Primary building volume — continuous shell + floor + openings"),
  /** Legacy room macro (mitred walls). Prefer `mass` for new cottages. Still used
   *  for multi-room / upper-storey inserts. */
  z.strictObject({
    op: z.literal("room"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    baseY: num().describe("floor height — 0 for ground, the storey height for upper rooms (slab auto-insets)"),
    wallH: num(z.number().positive()), wallT: num(z.number().positive()),
    wallColor: colorField, floorColor: colorField,
    floorThickness: num(z.number().positive()).optional(),
    /** Wall look: flat (default mitre), logs (cabin ribs), shell/plaster (continuous overlap like mass). */
    wallTreatment: z.enum(["flat", "logs", "shell", "plaster"]).optional(),
    logColor: colorField.optional(),
    plankFloor: z.boolean().optional(),
    plankWidth: num(z.number().positive()).optional(),
    openings: z.array(roomOpening).optional().describe("doors (≤1 per side) + windows; ground rooms should have a door"),
    ...whenGuard,
  }).describe("Room macro — prefer mass for free-standing houses"),
  z.strictObject({
    op: z.literal("wall"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    baseY: num(), height: num(z.number().positive()), color: colorField,
    collide: z.boolean().optional(),
    ...whenGuard,
  }),
  z.strictObject({
    op: z.literal("floor"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    y: num(), thickness: num(z.number().positive()), color: colorField,
    surfaceY: num().optional(),
    ...whenGuard,
  }),
  z.strictObject({
    op: z.literal("ceiling"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    y: num(), thickness: num(z.number().positive()), color: colorField,
    ...whenGuard,
  }),
  z.strictObject({
    op: z.literal("staircase"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    yLow: num(), yHigh: num(),
    axis: z.enum(["x", "z"]), lowAt: z.enum(["min", "max"]),
    color: colorField, steps: num(z.int().min(2)).optional(),
    ...whenGuard,
  }),
  /**
   * GABLE ROOF — the crown on a building.
   * Default `finish:"shingle"` keeps the classic many-course look (live cottages
   * unchanged). Opt into `finish:"mass"` for a continuous cohesive crown, or
   * `finish:"curve"` for a storybook cambered/kicked shell (concept B).
   */
  z.strictObject({
    op: z.literal("gableRoof"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    baseY: num().describe("wall top the eaves sit on"),
    ridgeAxis: z.enum(["x", "z"]).describe("horizontal axis the ridge runs along"),
    height: num(z.number().positive()).describe("ridge height above baseY"),
    eave: num(z.number().nonnegative()).optional().describe("overhang past the walls (default 0.9)"),
    steps: num(z.int().min(2)).optional().describe("pitch courses (mass default 4, shingle default 7)"),
    color: colorField, colorDk: colorField.optional(), ridgeColor: colorField.optional(),
    /**
     * "shingle" (default, BC) = many alternating courses;
     * "mass" = cohesive stepped crown;
     * "curve" = low-poly cambered shell + soft ridge/barge/eave trim.
     */
    finish: z.enum(["mass", "shingle", "curve"]).optional(),
    softRidge: z.boolean().optional().describe("rounded ridge beam + end-caps (default true for mass/curve)"),
    softEaves: z.boolean().optional().describe("eave fascia / eave rolls (default true for mass/curve)"),
    /** Curve finish: ridge arch amplitude in world units (default ~0.4). */
    camber: num(z.number().nonnegative()).optional(),
    /** Curve finish: eave upturn amplitude in world units (default ~0.5). */
    kick: num(z.number().nonnegative()).optional(),
    /** Curve finish: samples along the pitch ridge→eave (default 6). */
    pitchSegs: num(z.int().min(3).max(16)).optional(),
    /** Curve finish: samples along the ridge length (default 6). */
    alongSegs: num(z.int().min(3).max(16)).optional(),
    ...whenGuard,
  }),
  /**
   * BASE — foundation mass under a building. ONE solid pad (not four wall bands)
   * so the footing reads as a single ground mass. Optional stoop / multi-step
   * stairs on a face. `finish:"courses"` adds concept-B irregular stone blocks.
   */
  z.strictObject({
    op: z.literal("base"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    baseY: num().optional().describe("pad bottom (default −height)"),
    height: num(z.number().positive()).describe("pad thickness / rise"),
    outset: num(z.number().nonnegative()).optional().describe("how far past the mass footprint (default 0.45)"),
    color: colorField,
    colorDk: colorField.optional().describe("under-edge shade (optional second lip)"),
    /**
     * "solid" (default, BC) = smooth pad;
     * "courses" = irregular stone block courses around the pad (concept B).
     */
    finish: z.enum(["solid", "courses"]).optional(),
    /** Course count when finish is "courses" (default 2). */
    courses: num(z.int().min(1).max(6)).optional(),
    /** Door-side stoop: which face gets a step pad / stairs. */
    stoop: z.enum(["north", "south", "east", "west"]).optional(),
    stoopDepth: num(z.number().positive()).optional(),
    stoopWidth: num(z.number().positive()).optional(),
    /**
     * Multi-step stone stair on the stoop face (default 1 = single slab).
     * Concept B uses 3. Requires `stoop`.
     */
    steps: num(z.int().min(1).max(8)).optional(),
    ...whenGuard,
  }).describe("Foundation pad — solid ground mass under a building"),
  /** @deprecated alias of `base` — maps to a solid pad (thickness/softCorners ignored). */
  z.strictObject({
    op: z.literal("plinth"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    baseY: num().optional(),
    height: num(z.number().positive()),
    outset: num(z.number().nonnegative()).optional(),
    /** @deprecated ignored — base is a solid pad, not a band. */
    thickness: num(z.number().positive()).optional(),
    color: colorField,
    colorDk: colorField.optional(),
    /** @deprecated ignored — base is a solid pad. */
    softCorners: z.boolean().optional(),
    ...whenGuard,
  }),
  /** Exterior chimney stack piercing the roof. */
  z.strictObject({
    op: z.literal("chimney"),
    x: num(), z: num(),
    baseY: num().describe("stack bottom (usually into the roof mass)"),
    w: num(z.number().positive()).optional(),
    d: num(z.number().positive()).optional(),
    h: num(z.number().positive()),
    color: colorField,
    capColor: colorField.optional(),
    soft: z.boolean().optional(),
    ...whenGuard,
  }),
  z.strictObject({
    op: z.literal("crenellate"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    topY: num(), color: colorField, axis: z.enum(["x", "z"]),
    ...whenGuard,
  }),
  z.strictObject({
    op: z.literal("tower"),
    cx: num(), cz: num(), r: num(z.number().positive()),
    height: num(z.number().positive()), color: colorField, roofColor: colorField,
    ...whenGuard,
  }),
  /** A standalone framed window on any wall face (the room macro emits these for
   *  its window openings). `axis` = the face's run direction; (nx,nz) = its
   *  outward normal; `faceCoord` = the face's coordinate on the normal axis. */
  z.strictObject({
    op: z.literal("window"),
    axis: z.enum(["x", "z"]), along: num(), faceCoord: num(),
    nx: z.union([z.literal(0), z.literal(1), z.literal(-1)]),
    nz: z.union([z.literal(0), z.literal(1), z.literal(-1)]),
    cy: num(),
    w: num(z.number().positive()).optional(), h: num(z.number().positive()).optional(),
    frame: colorField.optional(), glass: colorField.optional(),
    /** "heavy" = concept-B chunky sill/jambs (matches mass timber package). */
    style: z.enum(["slim", "heavy"]).optional(),
    ...whenGuard,
  }),
  /** Interior prop from the furniture registry (src/structure/furniture). */
  z.strictObject({
    op: z.literal("furnish"),
    piece: z.string().min(1),
    x: num(), z: num(),
    /** Piece-specific knobs (y0, length, w, d, s, faceX, ceilingY…; shopSign
     *  also accepts string line1/line2 for the plaque text). */
    args: z.record(z.string(), z.union([num(), z.string()])).optional(),
    ...whenGuard,
  }),
  z.strictObject({ op: z.literal("light"), x: num(), y: num(), z: num(), ...whenGuard }),
  z.strictObject({ op: z.literal("dot"), x: num(), z: num(), color: colorField.optional(), ...whenGuard }),
  /** Invisible blockers. */
  z.strictObject({
    op: z.literal("collide"),
    minX: num(), maxX: num(), minZ: num(), maxZ: num(),
    minY: num(), maxY: num(),
    ...whenGuard,
  }),
  /** Raw decoration escape hatch — the model Part vocabulary at root. */
  z.strictObject({ op: z.literal("part"), parts: z.array(PartSchema).min(1), ...whenGuard }),
]);

/** One named variation of a style doc: a preset of param values. Ops that only
 *  belong to this variation gate on the params it sets via their `when` guard,
 *  so a variation can add/drop storeys, swap furniture, resize, etc. */
export const StructureVariantSchema = z.strictObject({
  params: z
    .record(z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, "param names are identifiers"), z.number())
    .optional()
    .describe("param overrides for this variation (keys must be declared in the doc's `params`)"),
  /** Shallow palette overrides — a pure recolor (e.g. cottage → stone) WITHOUT
   *  changing construction. Keep the style's build; only tint it. */
  palette: z.record(z.string(), paletteEntry).optional(),
  weight: z.number().positive().optional().describe("relative weight for seeded auto-pick when a placement omits `variant` (default 1)"),
});

export const StructureDocSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  /** Minimap footprint-dot color. */
  mapColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb"),
  palette: z.record(z.string(), paletteEntry).optional(),
  params: z
    .record(z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, "param names are identifiers"), z.number())
    .optional()
    .describe("named numeric knobs with their DEFAULTS — op fields may reference them as expressions, and placements may override them per instance"),
  /** Named variations (the STYLE↔VARIATION model): each is a param preset. A
   *  placement picks one by name (`variant`); when it doesn't, one is seed-picked
   *  by position. A doc with no `variants` behaves exactly as before. */
  variants: z.record(idField, StructureVariantSchema).optional(),
  /** Emit an automatic minimap footprint ring (from the union of the doc's room
   *  AABBs, in `mapColor`) — saves hand-authoring `dot` ops for a building. */
  footprintRing: z.boolean().optional(),
  ops: z.array(structureOp).min(1),
});

export type StructureDoc = z.infer<typeof StructureDocSchema>;
export type StructureOp = z.infer<typeof structureOp>;
export type StructureVariant = z.infer<typeof StructureVariantSchema>;

// World: factions + stance table (content/world/factions.json) ---------------------
// Plan 14: the faction list and who fights/assists whom. `player` is the
// implicit player-side faction id — it may appear in stance rows without being
// declared. Unlisted pairs are neutral; same faction is always friendly.
// Hydrated into the sim's StanceTable (src/sim/combat.ts stanceOf) by the
// server AND the studio's Skill Sim — the table is an argument, never imported.

export const WorldFactionsSchema = z.strictObject({
  $schema: z.string().optional(),
  factions: z
    .array(
      z.strictObject({
        id: idField,
        name: z.string().min(1),
        assistRadius: z.number().positive().optional()
          .describe("default assist-scan radius for members (NPC docs may override per template)"),
      }),
    )
    .min(1),
  stances: z.array(
    z.strictObject({
      a: z.string().min(1),
      b: z.string().min(1),
      stance: z.enum(["hostile", "neutral", "friendly"]),
    }).describe("order-independent pair — {a: guard, b: bandit, stance: hostile}"),
  ),
});

// Moves (content/moves/*.json) -------------------------------------------------------
// Plan 14: special combat moves — action-bar slots 2-3. A move rides the
// equipped weapon (weapons declare which moves they support via `weapon.moves`)
// and multiplies its stats; `clip` picks which existing attack clip animates it
// (bespoke per-move clips can come later via the studio timeline). Server-
// validated on use: the weapon must support it AND `requiresBox` (if set) must
// be unlocked — the plan-09 gate, wired from day one.

/** Special-move hit volume: sector (cone/circle via arcDeg) or line (pierce corridor).
 *  Primary light attack stays on weapon style→STYLE_ARC — only specials author shapes. */
export const MoveShapeSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("sector"),
    arcDeg: z.number().min(0).max(360).describe("frontal sector width degrees (360 = circle around caster)"),
    rangeMult: z.number().positive().describe("× the weapon's attackRange"),
  }),
  z.strictObject({
    kind: z.literal("line"),
    lengthMult: z.number().positive().describe("× the weapon's attackRange = corridor length"),
    width: z.number().positive().describe("full corridor width in world units"),
  }),
]);

export const MoveSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  name: z.string().min(1),
  icon: z.string().min(1).describe("action-bar slot icon (emoji)"),
  clip: z.enum(ATTACK_STYLES).describe("which attack clip animates it (content/anim/clips.json style)"),
  damageMult: z.number().positive().describe("× the weapon's damage"),
  cooldown: z.number().positive().describe("this slot's own cooldown, seconds (independent of the light attack's)"),
  staminaCost: z.number().nonnegative().describe("absolute stamina cost (not a multiplier)"),
  /** melee (default) = frontal shape cleave; ranged = focus fire / projectile path. */
  delivery: z.enum(["melee", "ranged"]).optional()
    .describe("melee = shape cleave (default); ranged = focus-target arrow (bow specials)"),
  /** instant (default) = press to fire; charge = hold key, release at full for Power Shot. */
  cast: z.enum(["instant", "charge"]).optional()
    .describe("instant = press once (default); charge = hold-to-charge, full only on release"),
  chargeTime: z.number().positive().optional()
    .describe("seconds to full charge when cast is charge (required for charge moves)"),
  shape: MoveShapeSchema.optional()
    .describe("hit volume for melee delivery — required when delivery is melee/omitted"),
  falloffMin: z.number().min(0).max(1).optional()
    .describe("damage fraction at max range (linear falloff from 1 at origin; default 0.5). Effects always full strength"),
  requiresBox: z.string().optional().describe("skill box id that must be unlocked to use this move (plan-09 gate)"),
  effects: z.array(CombatEffectSchema).optional().describe("crowd-control applied on each landed hit (snare/stun/slow/knockback)"),
}).superRefine((m, ctx) => {
  const delivery = m.delivery ?? "melee";
  if (delivery === "melee" && !m.shape) {
    ctx.addIssue({ code: "custom", message: "melee moves require shape", path: ["shape"] });
  }
  if ((m.cast ?? "instant") === "charge") {
    if (!(m.chargeTime && m.chargeTime > 0)) {
      ctx.addIssue({ code: "custom", message: "charge moves require chargeTime > 0", path: ["chargeTime"] });
    }
    // Only the ranged charge* wire path exists today — block content that can't run.
    if (delivery !== "ranged") {
      ctx.addIssue({
        code: "custom",
        message: 'cast "charge" currently requires delivery "ranged"',
        path: ["cast"],
      });
    }
  }
});

export type MoveDoc = z.infer<typeof MoveSchema>;


// Scenarios (content/scenarios/*.json) ----------------------------------------------
// Skill Sim presets (plan 14): a named spawn list the arena loads in one click —
// the regression bench for combat tuning, later a template for world encounters.
// Scenario ids are STUDIO CONVENIENCES, not persistence keys — renaming one is
// safe (unlike NPC spawn ids, nothing is stored under them).

export const ScenarioSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField.describe("studio convenience, NOT a persistence key — safe to rename"),
  name: z.string().min(1),
  notes: z.string().optional().describe("what this scenario exercises (shown in the picker)"),
  spawns: z
    .array(
      z.strictObject({
        npc: z.string().min(1).describe("NPC doc id (content/npcs) — the kind to spawn"),
        x: z.number().describe("arena-local coords (the player stands near the origin)"),
        z: z.number(),
        faction: idField.optional().describe("override the kind's faction for this spawn"),
      }),
    )
    .min(1),
});

export type ScenarioDoc = z.infer<typeof ScenarioSchema>;

// Cinematics (content/cinematics/*.json, plan 26) -----------------------------------
// A cutscene as CONTENT: an ordered SHOT LIST played by the client-only cinematic
// runtime (src/cinematic/*) over an isolated stage (a world built from `map` +
// throwaway puppet rigs — no server simulation). The SAME doc plays in the game
// (first-login intro, playCinematic talk actions) and the studio #/cinematics view.
//
// SEQUENCING CONTRACT (the runtime and every author rely on these five rules):
//  1. Shots play strictly in order; one camera setup per shot.
//  2. All of a shot's actions start together at shot start (offset by `delay`),
//     EXCEPT `say` — a shot's says form a queue, one line on screen at a time,
//     in doc order.
//  3. Shot end: at `duration` seconds if set (authoritative — a still-streaming
//     line insta-completes, no hold); else when every BLOCKING action finishes.
//     A shot with neither `duration` nor a blocking action is a validation error.
//  4. A non-blocking `walk` that outlives its shot CONTINUES across the cut
//     (movement is world-state — birdseye → tracking shots read naturally).
//     All other unfinished actions snap-complete at the cut.
//  5. A walking actor faces its travel direction; an explicit `face` wins once
//     its walk ends.
// Blocking defaults: walk/gesture/wait/fade block; face/pose don't; say ALWAYS blocks.

/** Where a camera looks / anchors: an actor id, several ids (live midpoint), or
 *  a fixed [x,z] world point (height resolved from the terrain). */
const cineActorRef = z.union([
  z.string().min(1).describe("actor id"),
  z.array(z.string().min(1)).min(1).describe("actor ids — the live midpoint"),
  z.tuple([z.number(), z.number()]).describe("fixed [x,z] world point"),
]);

/** Path-shot lookAt: an actor ref or a fixed [x,y,z] world point. */
const cineLookAt = z.union([
  z.string().min(1).describe("actor id"),
  z.array(z.string().min(1)).min(1).describe("actor ids — the live midpoint"),
  vec3.describe("fixed [x,y,z] world point"),
]);

const cineCamCommon = {
  transition: z.enum(["cut", "ease"]).optional().describe('how this shot arrives (default "ease")'),
  easeTime: z.number().positive().optional().describe("ease seconds (default 0.35)"),
};

/** One camera setup. Named shots re-frame LIVE actor anchors every frame (actors
 *  move); `path` is the raw escape hatch (CatmullRom spline, constant speed —
 *  requires the shot to set `duration`). Framing defaults live in one table:
 *  SHOT_DEFAULTS (src/cinematic/camera.ts). */
export const CineCameraSchema = z.discriminatedUnion("shot", [
  z.strictObject({
    shot: z.literal("birdseye"), target: cineActorRef,
    height: z.number().positive().optional().describe("camera height above the anchor (default 24)"),
    drift: z.number().optional().describe("slow lateral drift, units/s (cinematic float)"),
    ...cineCamCommon,
  }),
  z.strictObject({
    shot: z.literal("wide"), target: cineActorRef,
    dist: z.number().positive().optional(),
    azimuth: z.number().optional().describe("world-space viewing angle, radians"),
    height: z.number().optional(),
    ...cineCamCommon,
  }),
  z.strictObject({
    shot: z.literal("twoShot"), a: z.string().min(1), b: z.string().min(1),
    side: z.union([z.literal(1), z.literal(-1)]).optional().describe("which side of the a→b line"),
    dist: z.number().positive().optional().describe("extra distance from the pair"),
    ...cineCamCommon,
  }),
  z.strictObject({
    shot: z.literal("overShoulder"), from: z.string().min(1), to: z.string().min(1),
    side: z.union([z.literal(1), z.literal(-1)]).optional(),
    ...cineCamCommon,
  }),
  z.strictObject({
    shot: z.literal("closeUp"), target: z.string().min(1),
    azimuth: z.number().optional().describe("radians off face-on, relative to the actor's facing (default ~0.5 = ¾ portrait)"),
    height: z.number().optional().describe("lookAt height above feet (default 1.55 — the face)"),
    ...cineCamCommon,
  }),
  z.strictObject({
    shot: z.literal("follow"), target: cineActorRef,
    dist: z.number().positive().optional(),
    height: z.number().optional(),
    azimuth: z.number().optional().describe("radians off the travel direction (default 0 = trailing)"),
    ...cineCamCommon,
  }),
  z.strictObject({
    shot: z.literal("orbit"), target: cineActorRef,
    dist: z.number().positive().optional(),
    height: z.number().optional(),
    degPerSec: z.number().optional().describe("orbit rate (default 12)"),
    ...cineCamCommon,
  }),
  z.strictObject({
    shot: z.literal("path"),
    points: z.array(vec3).min(2).describe("CatmullRom control points, world [x,y,z] — fewer = smoother"),
    lookAt: cineLookAt,
    closed: z.boolean().optional(),
    ...cineCamCommon,
  }),
]);

const cineActCommon = {
  blocking: z.boolean().optional().describe("does the shot wait for this action?"),
  delay: z.number().nonnegative().optional().describe("seconds after shot start before this action begins"),
};

export const CineActionSchema = z.discriminatedUnion("do", [
  z.strictObject({
    do: z.literal("walk"), actor: z.string().min(1),
    path: z.array(z.tuple([z.number(), z.number()])).min(1).describe("[x,z] waypoints from the actor's current position"),
    gait: z.enum(["walk", "run"]).optional(),
    ...cineActCommon,
  }),
  z.strictObject({
    do: z.literal("face"), actor: z.string().min(1),
    toward: z.union([z.string().min(1), z.number()]).describe("actor id, or an absolute ry in radians"),
    ...cineActCommon,
  }),
  z.strictObject({
    do: z.literal("gesture"), actor: z.string().min(1),
    clip: z.string().min(1).describe('"primary" (equipped-weapon attack) or a moveClips key'),
    ...cineActCommon,
  }),
  // Switch an actor's held pose mid-scene (persists until the next pose action) —
  // e.g. slump → rousing → seated for "stirs and sits up". Instant + non-blocking
  // by default (it's a state set, like `face` it doesn't gate the shot).
  z.strictObject({
    do: z.literal("pose"), actor: z.string().min(1),
    to: z.string().min(1).describe("body pose id from content/anim/clips.json poses (slump/seated/rousing/…)"),
    ...cineActCommon,
  }),
  // say has NO blocking field on purpose — it always blocks (contract rule 2/3).
  z.strictObject({
    do: z.literal("say"), actor: z.string().min(1),
    text: z.string().min(1),
    rate: z.number().positive().optional().describe("chars/sec override (default ~28)"),
    delay: z.number().nonnegative().optional(),
  }),
  z.strictObject({ do: z.literal("wait"), seconds: z.number().positive(), delay: z.number().nonnegative().optional() }),
  z.strictObject({
    do: z.literal("fade"), dir: z.enum(["out", "in"]),
    ms: z.number().positive().optional().describe("fade milliseconds (default 250)"),
    ...cineActCommon,
  }),
]);

export const CineShotSchema = z.strictObject({
  camera: CineCameraSchema,
  actions: z.array(CineActionSchema).optional(),
  duration: z.number().positive().optional().describe("authoritative shot length (seconds); else the shot ends when every blocking action finishes"),
});

export const CineActorSchema = z.strictObject({
  id: idField,
  kind: z.string().min(1).describe('"player" = the viewer\'s own hero (live look + gear), else an NPC doc id (content/npcs)'),
  at: z.tuple([z.number(), z.number()]).describe("starting [x,z] on the stage map (feet height via the terrain)"),
  face: z.number().optional().describe("starting ry, radians"),
  weapon: z.string().optional().describe("item type with a weapon block, held for the scene"),
  /** Rest on the water surface (WATER_LEVEL) instead of the terrain, with a
   *  gentle buoyancy bob/roll + a bow wake — for boats/rafts (plan 26 boat
   *  arrival). Model y=0 is the WATERLINE (keel below, gunwales above); keep
   *  the interior FLOOR top above y=0 or water shows inside the hull. */
  float: z.boolean().optional().describe("actor floats on the water surface (bob + wake) instead of standing on terrain — boats/rafts"),
  /** Carried by another actor (a vehicle — e.g. a boat): the rider's world pose
   *  composes on the vehicle's transform + a local seat offset each frame, and
   *  its walk-gait is suppressed (it's being carried, not walking). The rider's
   *  own `at`/`walk` are ignored while ridden — the vehicle's `walk` moves both.
   *  `seat` is the FEET mark (usually floor top under a thwart); body-pose root
   *  (e.g. seated.root.y) still applies on top — see cinematic-authoring §3b. */
  ride: z.strictObject({
    of: z.string().min(1).describe("actor id of the vehicle this rider rides"),
    seat: vec3.describe("local [dx,dy,dz] FEET mark in the vehicle's frame (floor top under a seat)"),
    face: z.number().optional().describe("ry offset relative to the vehicle's facing (default 0)"),
  }).optional().describe("ride a vehicle actor — feet at seat, pose root applied, gait suppressed"),
  /** A named BODY POSE (content/anim/clips.json `poses`) held over the idle
   *  animation each frame — e.g. "slump" (unconscious) or "seated". Only the
   *  joints the pose defines are overridden; the rest keep the idle motion.
   *  Pose switches cross-fade (src/anim/body-pose.ts). Root offset (y drop)
   *  applies for free actors AND riders (composeRide layers it on ride.seat);
   *  root-free poses (slump) leave placement to ride.seat alone. */
  pose: z.string().min(1).optional().describe("named body pose (anim clips.json poses) — slump/seated/…; layered over idle"),
});

export const CinematicSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  name: z.string().min(1),
  map: idField.describe("the stage — any content/maps doc id"),
  skippable: z.boolean().optional().describe("Esc / Skip ▸ ends the whole cinematic (default false; click-to-advance always works)"),
  trigger: z.enum(["firstLogin"]).optional().describe("at most ONE doc may claim firstLogin"),
  letterbox: z.boolean().optional().describe("default true"),
  timeOfDay: z.number().min(0).max(1).optional().describe("hold the stage at this tod (0.5 = noon)"),
  actors: z.array(CineActorSchema).min(1),
  shots: z.array(CineShotSchema).min(1),
});

export type CinematicDoc = z.infer<typeof CinematicSchema>;
export type CineActor = z.infer<typeof CineActorSchema>;
export type CineShot = z.infer<typeof CineShotSchema>;
export type CineCamera = z.infer<typeof CineCameraSchema>;
export type CineAction = z.infer<typeof CineActionSchema>;
export type CineActorRef = z.infer<typeof cineActorRef>;

// World seeded ground items (embedded in a map doc's `items` — plan 20) -------------

export const WorldItemsSchema = z.array(
  z.strictObject({
    id: z.string().min(1),
    type: z.string().min(1),
    x: z.number(),
    y: z.number(),
    z: z.number(),
  }),
);

// Maps (content/maps/*.json, plan 20) ------------------------------------------------
// The world itself as CONTENT: one doc = one buildable map. Terrain composes as
// noise base → `features` (parametric ops, PRE-shore-carve so basins get natural
// banks) → carveShore → `brush` (sparse hand-sculpt deltas, POST-carve = WYSIWYG)
// → `flats` + structure pads (src/map/layers.ts holds the math). The map also
// owns its placements: structures/roads/items (moved from content/world),
// `decos` (hand-placed decos with STABLE ids — the old world/props, ids are the
// prop_state persistence keys), and `npcs` (NPC spawns of kind docs — plan 21;
// ids are the npc_state persistence keys). MULTI-MAP (plan 21): every doc is a
// live world the server simulates side-by-side; exactly one sets `default`.
// Authored by AI (Claude sessions) and the studio's #/world editor alike —
// keep fields compact + parametric.

/** One parametric elevation op. Folded SEQUENTIALLY in array order (plateau
 *  blends toward an absolute elevation, so order matters). `feather` defaults
 *  to 8 (FEATHER_DEFAULT in src/map/layers.ts). */
export const MapFeatureSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("hill"),
    x: z.number(), z: z.number(),
    r: z.number().positive(),
    feather: z.number().nonnegative().optional(),
    h: z.number().describe("height added at the core (negative = a dent)"),
  }),
  z.strictObject({
    kind: z.literal("basin"),
    x: z.number(), z: z.number(),
    r: z.number().positive(),
    feather: z.number().nonnegative().optional(),
    depth: z.number().positive().describe("depth subtracted at the core — carry it below the waterline for a lake"),
  }),
  z.strictObject({
    kind: z.literal("plateau"),
    x: z.number(), z: z.number(),
    r: z.number().positive(),
    feather: z.number().nonnegative().optional(),
    y: z.number().describe("absolute elevation the core is blended to"),
  }),
  z.strictObject({
    kind: z.literal("ridge"),
    points: z.array(z.tuple([z.number(), z.number()])).min(2).describe("[x,z] polyline along the crest"),
    w: z.number().positive().describe("crest width (full)"),
    feather: z.number().nonnegative().optional(),
    h: z.number().describe("height added along the crest (negative = a gully)"),
  }),
  z.strictObject({
    kind: z.literal("ramp"),
    x0: z.number(), z0: z.number(),
    x1: z.number(), z1: z.number(),
    w: z.number().positive(),
    feather: z.number().nonnegative().optional(),
    h0: z.number(), h1: z.number(),
  }),
]);

/** A flatten disc (the FlatZone shape) — level ground for pastures/camps.
 *  Structure pads are NOT listed here; buildWorld derives those per placement. */
export const MapFlatSchema = z.strictObject({
  x: z.number(), z: z.number(),
  r: z.number().positive(),
  feather: z.number().nonnegative(),
  y: z.number(),
});

/** The sparse hand-sculpt layer. Cells keyed "ix,iz" (world pos = ix*res,
 *  iz*res); value = height delta. Authored by the editor's brushes, applied
 *  bilinearly AFTER carveShore. */
export const MapBrushSchema = z.strictObject({
  res: z.number().min(1).max(10),
  cells: z.record(z.string().regex(/^-?\d+,-?\d+$/, `cell key "ix,iz"`), z.number()),
});

/** A region multiplier over the procedural deco scatter. `clear` empties it,
 *  `densify` multiplies placement density by `factor`, `bias` multiplies one
 *  deco type's pick weight by `factor`. */
export const ScatterMaskSchema = z.discriminatedUnion("shape", [
  z.strictObject({
    shape: z.literal("circle"),
    x: z.number(), z: z.number(), r: z.number().positive(),
    feather: z.number().nonnegative().optional(),
    effect: z.enum(["clear", "densify", "bias"]),
    factor: z.number().positive().max(8).optional().describe("densify/bias multiplier, default 2"),
    deco: z.string().min(1).optional().describe("bias target deco type"),
  }),
  z.strictObject({
    shape: z.literal("rect"),
    minX: z.number(), maxX: z.number(), minZ: z.number(), maxZ: z.number(),
    feather: z.number().nonnegative().optional(),
    effect: z.enum(["clear", "densify", "bias"]),
    factor: z.number().positive().max(8).optional(),
    deco: z.string().min(1).optional(),
  }),
]);

/** A reusable named scatter composition (content/scatter-palettes/<id>.json) —
 *  a "scatter type" a map references by id. `entries` lists the eligible deco
 *  types + their pick weights; the scatter still habitat-sorts them, so a
 *  land-only palette leaves shore/water bare. */
export const ScatterPaletteSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  name: z.string().min(1),
  entries: z
    .array(z.strictObject({ deco: z.string().min(1), weight: z.number().nonnegative() }))
    .min(1),
});

export type ScatterPaletteDoc = z.infer<typeof ScatterPaletteSchema>;

/** A hand-placed deco with a STABLE authored id (replaces world/props.json —
 *  existing chest ids preserved). Interactable types keep working exactly as
 *  props did (the id is the prop_state persistence key — never rename one once
 *  shipped); non-interactable types are legitimate landmark placements. */
export const MapDecoSchema = z.strictObject({
  id: idField.describe("PERSISTENCE KEY for interactable state — never rename once shipped"),
  type: z.string().min(1).describe("a deco type"),
  x: z.number(),
  z: z.number(),
  /** Yaw in radians. Default 0. */
  rot: z.number().optional(),
  scale: z.number().positive().optional(),
  /** Intentional indoor placement (kitchen range, shop counter props). Suppresses
   *  the false "deco-inside" warn when the point is actually inside a building;
   *  ERROR if flagged indoors but lands outdoors. */
  indoors: z.boolean().optional().describe("true = intentional indoor prop — must sit inside a cottage-scale building"),
});

/** Per-map size bounds (plan 21). The floor is one terrain chunk (~135u); the
 *  ceiling is the eager-build limit — raising it is plan 06 (streamed worldgen),
 *  not a knob. */
export const MIN_MAP_SIZE = 135;
export const MAX_MAP_SIZE = 2160;

/** Per-map environment overrides (plan 21). All optional — an omitted field
 *  keeps the global behavior. */
export const MapEnvSchema = z.strictObject({
  /** Water plane height. Feeds the shore-carve too, so banks re-derive. */
  waterLevel: z.number().optional().describe("water plane height (default -1.5) — the shore-carve pivots on it"),
  /** false = this map never receives weather (indoor/arena maps). Default true. */
  weather: z.boolean().optional().describe("false = no rain/snow/storms on this map (default true)"),
  /** Lock time-of-day: 0 = midnight, 0.25 = sunrise, 0.5 = noon, 0.75 = sunset. */
  todLock: z.number().min(0).max(1).optional().describe("freeze the sun: 0 midnight, 0.25 sunrise, 0.5 noon, 0.75 sunset"),
  skyTint: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().describe("sky color tint (#rrggbb), multiplied over the day-night sky"),
  fogTint: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().describe("fog color tint (#rrggbb), multiplied over the day-night fog"),
});

/** An NPC placement — a spawn of a kind (an npc doc) on THIS map (plan 21;
 *  spawns moved here from the npc docs' `spawns` arrays — ids kept verbatim). */
export const MapNpcSpawnSchema = z.strictObject({
  /** Unique across ALL maps — persisted npc_state is keyed by it. */
  id: idField.describe("PERSISTENCE KEY — unique across all maps, NEVER rename or reuse (death/respawn state + kill credit key off it)"),
  npc: idField.describe("the npc doc id (the kind) this spawn instantiates"),
  x: z.number(),
  /** Ballpark feet height; snapped to floor/terrain at runtime. Default 0. */
  y: z.number().optional().describe("ballpark feet height — snapped to floor/terrain at runtime (~10 for a castle roof)"),
  z: z.number(),
  facing: z.number().optional(),
  /** Per-spawn overrides of the kind template. */
  name: z.string().min(1).optional(),
  dialogue: z.array(z.string().min(1)).min(1).optional(),
  roam: RoamSchema.optional(),
  /** Authored idle body pose (content/anim/clips.json `poses`) — a seated
   *  innkeeper, a kneeling penitent. Per-SPAWN on purpose (the kind doc has no
   *  pose field). Cleared at runtime the moment the NPC moves/fights/flees. */
  pose: z.string().min(1).optional().describe("idle body pose id (anim clips.json poses) — seated/kneeling/…; held until the NPC moves"),
  /** Intentional indoor spawn (cook at range, shopkeeper behind counter).
   *  Suppresses the false "spawn-inside" warn when feet are inside a building;
   *  ERROR if flagged indoors but the point is outdoors. */
  indoors: z.boolean().optional().describe("true = intentional indoor NPC — must stand inside a cottage-scale building"),
});

/** A point portal (plan 24): a proximity trigger that fires transferCharacter.
 *  Stand within `r` of (x,z) → travel to map `to`, arriving at `spawn` (falls
 *  back to the target's defaultSpawn). Pair one on each side (with spawns clear
 *  of each other's trigger radius) for a bidirectional gate. */
export const MapPortalSchema = z.strictObject({
  /** Studio selection/status handle — NOT a persistence key. */
  id: idField.optional(),
  x: z.number(),
  z: z.number(),
  r: z.number().positive().describe("trigger radius (world units)"),
  to: z.string().min(1).describe("target map doc id"),
  spawn: z.strictObject({
    x: z.number(),
    z: z.number(),
    ry: z.number().optional(),
  }).optional().describe("arrival point on the target (default: its defaultSpawn)"),
});

/** A fish population zone (Living Waters rework): a radius disc over a body of
 *  water. Fish swim within zone ∩ actual water (heightAt < waterLevel −
 *  WATER_MARGIN), so a zone larger than its lake is fine. `id` is a stable
 *  AUTHORING key — runtime fish ids are transient and nothing persists, so
 *  renames are safe (unlike spawn/deco ids). */
export const MapWaterSchema = z.strictObject({
  id: idField.describe("stable authoring key (selection handle) — NOT a persistence key; renaming is safe"),
  x: z.number(),
  z: z.number(),
  r: z.number().positive().describe("zone radius (world units) — fish swim within zone ∩ actual water"),
  population: z.array(z.strictObject({
    fish: z.string().min(1).describe("a fish item type (a content/items doc carrying a `fish` block)"),
    count: z.int().positive().describe("individuals of this species living in the zone"),
  })).min(1),
  respawn: z.number().positive().optional().describe("seconds for one caught fish to respawn (default: tunables fishRespawn)"),
});

/** An ambient AUDIO zone: a radius disc that fades a looped track in as the
 *  player approaches. Full volume inside `inner`, silent at/past `r`, smooth
 *  crossfade between. Zone layers are ADDITIVE over the map's base `ambience`
 *  track — except `music: true` zones (a town's soundtrack), which duck the
 *  base ambience out fully while they're up. Tracks are plain audio files at
 *  assets/audio/<track>.opus or .mp3 (served at /audio/<track>.<ext>; the
 *  client tries opus first, then mp3) — no content doc.
 *  `id` is a stable AUTHORING key (rename-safe, nothing persists against it). */
export const MapAudioZoneSchema = z.strictObject({
  id: idField.describe("stable authoring key (selection handle) — NOT a persistence key; renaming is safe"),
  x: z.number(),
  z: z.number(),
  r: z.number().positive().describe("outer fade radius (world units) — silent at/past this"),
  inner: z.number().min(0).describe("full-volume radius — must be < r; the r−inner band is the crossfade"),
  track: z.string().regex(/^[\w-]+$/).describe("audio track name — assets/audio/<track>.opus or .mp3"),
  music: z.boolean().optional().describe("true = a MUSIC layer that ducks the base ambience out fully while up"),
  volume: z.number().min(0).max(1).optional().describe("layer volume trim 0..1 (default 1)"),
});

/** Whole-edge links (plan 24): walking into a linked border transfers to the
 *  neighbor's OPPOSITE edge, preserving the along-edge coordinate + facing —
 *  the arrival is fully rule-computed (no per-portal data). Validation
 *  enforces equal sizes + reciprocity (A.east→B requires B.west→A). North is
 *  −z. Adjacency is a maintained fiction, not an engine concept. */
const EdgeLinkSchema = z.strictObject({ to: z.string().min(1) });
export const MapEdgesSchema = z.strictObject({
  west: EdgeLinkSchema.optional(),
  east: EdgeLinkSchema.optional(),
  north: EdgeLinkSchema.optional(),
  south: EdgeLinkSchema.optional(),
});

export const EDGE_SIDES = ["west", "east", "north", "south"] as const;
export type EdgeSide = (typeof EDGE_SIDES)[number];
export const OPPOSITE_SIDE: Record<EdgeSide, EdgeSide> = {
  west: "east", east: "west", north: "south", south: "north",
};

export const MapSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  name: z.string().min(1),
  /** Square side length, world units. Per-map since plan 21 (135..2160; the
   *  ceiling is the eager-build limit — see MAX_MAP_SIZE). */
  size: z.number().min(MIN_MAP_SIZE).max(MAX_MAP_SIZE),
  /** Exactly ONE map doc sets this — new characters (and characters whose map
   *  vanished) land here. Validate enforces the exactly-one. */
  default: z.boolean().optional().describe("the default map — exactly one doc sets true"),
  /** Where transferred/orphaned characters appear (defaults to the origin). */
  defaultSpawn: z.strictObject({
    x: z.number(),
    z: z.number(),
    ry: z.number().optional(),
  }).optional().describe("arrival point for map transfers (default 0,0)"),
  /** Where a BRAND-NEW character starts (only new characters — transfers/orphans
   *  still use defaultSpawn). Lets the first-login intro deposit the hero somewhere
   *  other than the origin — e.g. on the dock beside Dekkar after the boat arrival
   *  (plan 26). Default: defaultSpawn. */
  introSpawn: z.strictObject({
    x: z.number(),
    z: z.number(),
    ry: z.number().optional(),
  }).optional().describe("new-character start (default: defaultSpawn) — for a first-login intro drop-off"),
  env: MapEnvSchema.optional(),
  terrain: z.strictObject({
    seed: z.int().describe("deco-scatter seed (buildWorld)"),
    noiseOffset: z.tuple([z.number(), z.number()]).optional().describe("fbm domain shift — [0,0] samples the classic noise"),
    amp: z.number().min(0).max(4).optional().describe("noise amplitude multiplier — 1 = default rolling hills, 0 = a perfectly flat base (features still carve into it)"),
    features: z.array(MapFeatureSchema),
    flats: z.array(MapFlatSchema),
    brush: MapBrushSchema.optional(),
  }),
  scatterMasks: z.array(ScatterMaskSchema).optional(),
  /** Freeform painted density field over the procedural scatter (the studio
   *  Scatter brush). Same sparse-grid shape as `terrain.brush`, but each cell
   *  is a DENSITY DELTA `d` → the scatter keep-rate is multiplied by
   *  max(0, 1 + d): −1 fully clears, +1 doubles. Absent → density ×1 (classic
   *  scatter). Composes multiplicatively with `scatterMasks`. */
  scatterBrush: MapBrushSchema.optional(),
  /** Map scatter COMPOSITION — a `scatter-palettes` doc id (a reusable named
   *  scatter type). When set, ONLY that palette's deco types scatter (still
   *  habitat-filtered), weighted by the palette; omitted → the global weight>0
   *  pool. A habitat with no listed type gets nothing (e.g. a trees-only
   *  palette leaves shore/water bare). Cross-ref validated in validate.ts. */
  scatterPalette: z.string().min(1).optional(),
  decos: z.array(MapDecoSchema),
  structures: WorldStructuresSchema,
  roads: WorldRoadsSchema,
  items: WorldItemsSchema,
  npcs: z.array(MapNpcSpawnSchema),
  /** Point portals (plan 24) — proximity-triggered map transfers. */
  portals: z.array(MapPortalSchema).optional(),
  /** Fish population zones (Living Waters) — authored in the #/world Waters tool. */
  waters: z.array(MapWaterSchema).optional(),
  /** The map-wide BASE ambience track (assets/audio/<name>.opus or .mp3) —
   *  loops everywhere on this map; audio zones layer over it. Omit for silence. */
  ambience: z.string().regex(/^[\w-]+$/).optional(),
  /** Ambient audio zones — proximity-faded loops authored in the #/world Audio tool. */
  audioZones: z.array(MapAudioZoneSchema).optional(),
  /** Whole-edge neighbor links (plan 24) — walk off a linked border to cross. */
  edges: MapEdgesSchema.optional(),
});

export type MapDoc = z.infer<typeof MapSchema>;
export type MapNpcSpawn = z.infer<typeof MapNpcSpawnSchema>;
export type MapEnv = z.infer<typeof MapEnvSchema>;
export type MapPortal = z.infer<typeof MapPortalSchema>;
export type MapWater = z.infer<typeof MapWaterSchema>;
export type MapAudioZone = z.infer<typeof MapAudioZoneSchema>;
export type MapEdges = z.infer<typeof MapEdgesSchema>;

// Animation clips (content/anim/clips.json) -----------------------------------------
// Mirrors TuningData in src/anim/clips.ts: a per-weapon animation set (attack
// clip + carry/ready stances + hold override) keyed by ItemType, the unarmed
// fists, and a shared move-clip library — all driving the shared humanoid rig
// contract (src/model/rig.ts): the player AND every humanoid NPC. Channels derive
// from the rig's joint list so the schema can't drift from the animator.

const POSE_CHANNELS = [...RIGS.humanoid.joints, "grip"] as const;

const KeyframeSchema = z
  .object({ t: z.number().min(0).max(1) })
  .catchall(vec3)
  .superRefine((k, ctx) => {
    for (const key of Object.keys(k)) {
      if (key !== "t" && !(POSE_CHANNELS as readonly string[]).includes(key)) {
        ctx.addIssue({ code: "custom", path: [key], message: `unknown channel (have: ${POSE_CHANNELS.join(", ")})` });
      }
    }
  });

const AttackClipSchema = z
  .strictObject({
    duration: z.number().positive(),
    keys: z.array(KeyframeSchema).min(1),
  })
  .superRefine((c, ctx) => {
    for (let i = 1; i < c.keys.length; i++) {
      if (c.keys[i]!.t < c.keys[i - 1]!.t) {
        ctx.addIssue({ code: "custom", path: ["keys", i, "t"], message: "keys must ascend by t" });
      }
    }
  });

const StancePoseSchema = z.partialRecord(z.enum(POSE_CHANNELS), vec3);

const HoldOverrideSchema = z.strictObject({
  rot: vec3.optional(),
  offset: vec3.optional(),
  scale: z.number().positive().optional(),
}).describe(
  "Rare override of item hold rot/offset/scale — NEVER hilt. Prefer fixing item hold; use this only when anim truly needs a different base orientation. Clip/stance grip is a small delta on top",
);

/** Coupled upper-body walk/run rock for two-hand carry (pose.ts). Hands move as
 *  a unit so the shaft doesn't shear. `false` = lock arms (no rock). Omit = no
 *  rock unless the runtime has a legacy default — prefer explicit docs. */
const CarrySwaySchema = z.strictObject({
  torsoY: z.number().optional().describe("torso yaw counter-swing scale (polearm ~0.07)"),
  torsoZ: z.number().optional().describe("torso roll scale"),
  torsoX: z.number().optional().describe("footfall pitch bob scale"),
  rockMul: z.number().optional().describe("shoulder same-phase rock × armAmp"),
  elbowMul: z.number().optional().describe("elbow give on back half of rock × armAmp"),
  headY: z.number().optional().describe("head yaw counter"),
  headZ: z.number().optional().describe("head roll counter"),
});

// One archetype's complete, self-contained animation set — light-attack motion
// + carry/ready stances + hold override (WeaponAnim in src/anim/clips.ts).
// Keyed by WeaponArchetypeId; every item with weapon.archetype = X shares it.
const WeaponAnimSchema = z.strictObject({
  hold: HoldOverrideSchema.optional(),
  attack: AttackClipSchema,
  carry: StancePoseSchema.optional(),
  ready: StancePoseSchema.optional(),
  /** Walk/run upper-body rock. Carry: full arms+torso. Combat-ready: torso/head
   *  (and 1H free hand) at ~half strength — weapon arm(s) stay planted. */
  carrySway: z.union([CarrySwaySchema, z.literal(false)]).optional(),
});

/** Support-contact kinds a body pose declares — which parts rest on the seat/
 *  ground. Drives the pose-seat diagnostics (inspect-model) and seat math. */
export const POSE_CONTACTS = ["pelvis", "knees", "hands", "feet", "torso"] as const;

// A NAMED FULL-BODY POSE (seated / sitting / prone / all-fours / kneeling /
// cinematic holds) — BodyPose in src/anim/clips.ts. Unlike carry/ready stances
// these can move the ROOT: the rig root is the FEET with the pelvis rigidly
// ~1.06·scale·legLen above it, so a real sit/lie needs a root Y drop (+ pitch
// for prone/all-fours) on top of the joint pose. Applied as a blended override
// layer (src/anim/body-pose.ts) — the caller integrates `root` over feetY.
const BodyPoseSchema = z.strictObject({
  /** The joint pose — same sparse channel map as carry/ready stances. Gameplay
   *  poses should define ALL rig joints (a held pose reads as stillness);
   *  cinematic holds may stay sparse so idle sway shows through. */
  joints: StancePoseSchema,
  root: z
    .strictObject({
      y: z.number().describe("root Y offset, ADDITIVE over feetY (negative = drop toward the ground/seat)"),
      pitch: z.number().optional().describe("root rotation.x radians (prone/all-fours tip forward); facing yaw is never pose data"),
      roll: z.number().optional().describe("root rotation.z radians"),
    })
    .optional(),
  fade: z
    .strictObject({
      enter: z.number().positive().describe("seconds to blend INTO this pose"),
      exit: z.number().positive().describe("seconds to blend back out to standing"),
    })
    .optional()
    .describe("cross-fade windows; omitted → POSE_BLEND defaults (src/anim/body-pose.ts)"),
  contacts: z
    .array(z.enum(POSE_CONTACTS))
    .optional()
    .describe("body parts resting on the support surface — drives inspect-model pose-seat WARNs"),
  seatable: z.boolean().optional().describe("THE furniture-sit pose — click-to-sit applies it"),
  cinematic: z.boolean().optional().describe("cutscene-only: excluded from chat emotes + the studio pose row"),
  enterClip: z.string().optional().describe("RESERVED (unimplemented): moveClips id for an authored enter transition"),
});

export const AnimClipsSchema = z.strictObject({
  $schema: z.string().optional(),
  // "humanoid" = the shared rig contract; "player" is the legacy name for the
  // same joint set (normalized on load in src/anim/clips.ts).
  rig: z.enum(["humanoid", "player"]),
  // The fists — jab + optional combat-ready (boxer guard) / idle stances.
  unarmed: z.strictObject({
    attack: AttackClipSchema,
    ready: StancePoseSchema.optional(),
    carry: StancePoseSchema.optional(),
  }),
  // Per-archetype animation (one-hand-sword / bow / …). Legacy key `weapons`
  // (per ItemType) is rejected — migrate keys into archetypes.
  archetypes: z.record(z.string(), WeaponAnimSchema),
  // Shared special-move motions the content/moves `clip` field references.
  moveClips: z.record(z.string(), AttackClipSchema),
  // Named full-body poses (seated/sitting/prone/all-fours/kneeling + cinematic
  // holds). Ids are wire strings (NetPlayer/NetNpc.pose) — kebab-case, and NEVER
  // renamed casually: map spawn records and cinematic docs reference them.
  poses: z.record(idField, BodyPoseSchema).optional(),
});

// Skills ----------------------------------------------------------------------
// A skill is a shallow tree of boxes (qualitative unlocks) gated by per-bar XP,
// prereqs, and a top-tier track-rank fiction gate. XP thresholds per bar live in
// the doc (data — rebalance without a code change). Mirrors SkillSpec in
// src/skill/define.ts. Cross-refs (patron↔npc, track↔standing, prereq↔box) are
// checked in validate.ts; grants.attack names an attack STYLE (declarative — not
// yet wired to a runtime effect), so it's an enum here.

// Bounds keep a content typo from becoming a ×26 damage mult or free 70% DR.
const BoxStatSchema = z.strictObject({
  maxHp: z.number().min(0).max(100).optional(),
  /** Outgoing damage % points (25 → ×1.25); per-box soft cap 100. */
  damage: z.number().min(0).max(100).optional(),
  stamina: z.number().min(0).max(50).optional(),
  /** Incoming armor % points (same units as wear.armor); per-box soft cap 30. */
  armor: z.number().min(0).max(30).optional(),
}).partial();

const SkillBoxSchema = z.strictObject({
  id: idField,
  name: z.string().min(1),
  branch: z.string().optional(),
  requires: z.strictObject({
    bar: z.string().min(1).describe("which xp bar gates this box (e.g. melee/ranged)"),
    xp: z.int().nonnegative(),
    prereq: z.array(z.string().min(1)).optional().describe("box ids that must be unlocked first"),
    rank: z
      .strictObject({ track: z.string().min(1), rank: z.int().nonnegative() })
      .optional()
      .describe("fiction gate: must hold this track rank (top-tier boxes only)"),
  }),
  cost: z.int().positive().default(1),
  grants: z.strictObject({
    attack: z.enum(ATTACK_STYLES).optional().describe("unlocks this attack style as an action"),
    weaponType: z.string().optional().describe("weapon type this box lets you wield"),
    move: z.string().min(1).optional().describe(
      "special-move id this box unlocks (Journal chip); move doc requiresBox must equal this box id",
    ),
    stat: BoxStatSchema.optional().describe("passive stat bumps, summed server-side"),
    fishing: z.strictObject({
      reelWindowMul: z.number().positive().optional().describe("× the angling reel window (multiplies across boxes)"),
      tableTier: z.int().nonnegative().optional().describe("+ angling table-tier access (sums across boxes)"),
      netCount: z.int().nonnegative().optional().describe("+ deployed nets allowed at once (sums)"),
      soakMul: z.number().positive().optional().describe("× the net soak time (multiplies — <1 is faster)"),
    }).optional().describe("fishing effects (plan 10) — folded by fishingSum(): *Mul fields multiply, the rest sum. The box's `branch` says which loop it aids"),
    hunting: z.strictObject({
      aimHitBonus: z.number().optional().describe("+ to the stalked shot's band hit%% (sums across boxes, capped at AIM_HIT_CAP)"),
      trophyTier: z.int().nonnegative().optional().describe("+ trophy-tier access (sums — scales rare-trophy odds via trophyChance)"),
      trapCount: z.int().nonnegative().optional().describe("+ deployed traps allowed at once (sums)"),
      soakMul: z.number().positive().optional().describe("× the trap soak time (multiplies — <1 is faster)"),
    }).optional().describe("hunting effects (plan 11) — folded by huntingSum(): *Mul fields multiply, the rest sum. The box's `branch` says which loop it aids"),
    cooking: z.strictObject({
      panWindowMul: z.number().positive().optional().describe("× the pan minigame's prompt hit window (multiplies across boxes — >1 is more forgiving)"),
      healBonus: z.int().nonnegative().optional().describe("+ HP a cooked dish restores when eaten (sums across boxes)"),
      brewCount: z.int().nonnegative().optional().describe("+ deployed fermenters allowed at once (sums)"),
      soakMul: z.number().positive().optional().describe("× the brew soak time (multiplies — <1 is faster)"),
    }).optional().describe("cooking effects (plan 18) — folded by cookingSum(): *Mul fields multiply, the rest sum. The box's `branch` says which loop it aids"),
  }),
});

export const SkillSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  name: z.string().min(1),
  /** Patron NPC spawn id (recognition source) + its standing track. */
  patron: z.string().min(1),
  track: z.string().min(1),
  /** XP bars this skill grows (Combat = ["melee","ranged"], single-bar skills = 1). */
  xpBars: z.array(z.string().min(1)).min(1),
  /** Per-skill investment cap (5 boxes = mastery). */
  capBoxes: z.int().positive().default(5),
  branches: z.array(z.string()).optional(),
  /** Per-bar XP curve: bar name → ascending thresholds (data; user-tunable). */
  thresholds: z.partialRecord(z.string(), z.array(z.int().nonnegative())).optional(),
  /** Display names for each XP rung, same order as `thresholds[bar]`. Optional. */
  rungNames: z.partialRecord(z.string(), z.array(z.string().min(1))).optional(),
  boxes: z.array(SkillBoxSchema).min(1),
});

export type SkillDoc = z.infer<typeof SkillSchema>;

// Standing ---------------------------------------------------------------------
// A patron's title ladder. Reaching a rank grants influence into the global pool
// (grantsPoints) and unlocks its title. progress is the cumulative meter that
// crosses into each rank. Mirrors StandingSpec in src/skill/define.ts.

export const StandingSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  name: z.string().min(1),
  /** Patron NPC spawn id. */
  patron: z.string().min(1),
  ranks: z
    .array(
      z.strictObject({
        name: z.string().min(1).describe("the title shown when at this rank"),
        progress: z.int().nonnegative().describe("cumulative meter needed to reach this rank"),
        grantsPoints: z.int().nonnegative().default(1),
      }),
    )
    .min(1),
});

export type StandingDoc = z.infer<typeof StandingSchema>;

// Catch tables (content/catch-tables/*.json) ----------------------------------
// Plan 10: weighted fish tables — one SINGLE pick per angling catch, several
// independent picks per netting haul (src/sim/fishing.ts pickWeighted). Tiered:
// the server maps water depth + unlocked boxes to a table id ("angling-tier0",
// "netting-bulk", …); CP2's authored fishing spots will name their own.

export const CatchTableSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField,
  entries: z
    .array(
      z.strictObject({
        type: z.string().min(1).describe("item type (content/items) this pick lands"),
        weight: z.number().positive().describe("relative pick weight (any scale — normalized at roll time)"),
      }),
    )
    .min(1),
});

export type CatchTableDoc = z.infer<typeof CatchTableSchema>;

// Tunables (content/tunables/*.json) -------------------------------------------
// Per-skill pacing/economy numbers, one doc per skill (id = the skill). The sim
// modules (src/sim/*) keep the DEFAULTS — a doc overrides them field-by-field
// via src/tunables.ts (the one merge point the server handlers, client HUD, and
// studio Skill-Sim all read). This is what makes the studio-first authoring loop
// real: tune a value in the Skill-Sim → Apply → the doc → the game reads it on
// next boot. Every field is optional — an absent field means "the sim default".
// strictObject so a typo'd knob fails validation instead of silently no-opping.
// Growing a skill's knob set = add fields here + read them in src/tunables.ts.

export const TunablesSchema = z.strictObject({
  $schema: z.string().optional(),
  id: idField.describe("the skill this doc tunes (e.g. \"fishing\")"),
  // -- angling (Living Waters rework; defaults in src/sim/fishing.ts) --
  castRange: z.number().positive().optional().describe("default freeform cast reach (a rod's fishing.castRange overrides)"),
  castCd: z.number().nonnegative().optional().describe("seconds between accepted cast intents (rate gate)"),
  // -- the encounter (notice → approach → investigate → nibble → bite) --
  noticeRadius: z.number().positive().optional().describe("u — a wandering fish inside this of the bobber may notice it"),
  noticeEvery: z.number().positive().optional().describe("s between the cast's notice scans"),
  investigateMin: z.number().positive().optional().describe("s min investigate budget (uniform roll, × fish patience/PATIENCE)"),
  investigateMax: z.number().positive().optional().describe("s max investigate budget"),
  nibbleMin: z.number().int().nonnegative().optional().describe("min nibble tugs before the bite (a fish's nibblesMin overrides)"),
  nibbleMax: z.number().int().nonnegative().optional().describe("max nibble tugs before the bite (a fish's nibblesMax overrides)"),
  nibbleDur: z.number().positive().optional().describe("s a nibble tug holds"),
  nibbleGapMin: z.number().positive().optional().describe("s min between investigate decisions (nibble or bite)"),
  nibbleGapMax: z.number().positive().optional().describe("s max between investigate decisions"),
  nibbleSpook: z.number().min(0).max(1).optional().describe("chance a pull DURING a nibble spooks the fish (a nibble pull never hooks)"),
  pullSpook: z.number().min(0).max(1).optional().describe("chance a pull while approaching/investigating spooks the fish"),
  biteWindow: z.number().positive().optional().describe("s grab window once the bite lands (× box reelWindowMul)"),
  spookCd: z.number().positive().optional().describe("s a spooked fish ignores bobbers"),
  loseCd: z.number().positive().optional().describe("s a bored fish ignores bobbers"),
  waryBump: z.number().min(0).max(1).optional().describe("wariness added on a spook (half on mere boredom); scales future notice rolls down"),
  waryDecay: z.number().nonnegative().optional().describe("wariness shed per second while wandering"),
  // -- population --
  fishRespawn: z.number().positive().optional().describe("s for one caught fish to respawn (a water zone's respawn overrides)"),
  shadowDepthOffset: z.number().nonnegative().optional().describe("u below the surface the client draws fish shadows"),
  // -- the reel minigame --
  reelArrowsBase: z.number().int().positive().optional().describe("arrows in the pattern before tier/fight scaling"),
  reelArrowsPerTier: z.number().int().nonnegative().optional().describe("+ arrows per fish tier"),
  reelGapBase: z.number().positive().optional().describe("s between arrows at tier 0"),
  reelGapMin: z.number().positive().optional().describe("hard gap floor (playability)"),
  reelSpeedRamp: z.number().nonnegative().optional().describe("gap divisor grows 1 + tier×this"),
  reelHitWindow: z.number().positive().optional().describe("± s a press counts as a hit (× box reelWindowMul)"),
  reelMissTension: z.number().min(0).max(1).optional().describe("tension added per missed arrow (1.0 = the fish escapes)"),
  reelHitRelief: z.number().min(0).max(1).optional().describe("tension shed per hit arrow"),
  reelInputSlack: z.number().nonnegative().optional().describe("s of late-arrival (network-latency) grace the server allows a reelInput hit past its arrow window"),
  reelDeadlinePad: z.number().nonnegative().optional().describe("s past the pattern's end before the server force-resolves an AFK reel"),
  // -- netting --
  soakTime: z.number().positive().optional().describe("default net soak seconds (a net's net.soakTime overrides; × box soakMul)"),
  netCapacityMin: z.number().int().positive().optional().describe("min fish per haul (uniform roll)"),
  netCapacityMax: z.number().int().positive().optional().describe("max fish per haul (uniform roll)"),
  netCount: z.number().int().positive().optional().describe("deployed nets allowed at once (+ box netCount)"),
  netRange: z.number().positive().optional().describe("max reach to deploy / collect a net (xz units)"),
  // -- xp + patron deeds --
  anglingXp: z.record(z.string(), z.number().positive()).optional().describe("angling XP per landed fish, keyed by item type"),
  anglingXpDefault: z.number().positive().optional().describe("angling XP for a fish not in anglingXp"),
  nettingXpPerFish: z.number().positive().optional().describe("netting XP per fish landed in a haul"),
  fishmongerPerCatch: z.number().nonnegative().optional().describe("Fishmonger standing per angling catch"),
  fishmongerPerHaul: z.number().nonnegative().optional().describe("Fishmonger standing per net haul"),
  // -- hunting: trapping (plan 11; defaults in src/sim/hunting.ts) --
  trapSoak: z.number().positive().optional().describe("default trap soak seconds (a trap's trap.soakTime overrides; × box soakMul)"),
  trapCapacityMin: z.number().int().positive().optional().describe("min small game per trap haul (uniform roll; a trap's capacityMin overrides)"),
  trapCapacityMax: z.number().int().positive().optional().describe("max small game per trap haul (a trap's capacityMax overrides)"),
  trapCount: z.number().int().positive().optional().describe("deployed traps allowed at once (+ box trapCount)"),
  trapRange: z.number().positive().optional().describe("max reach to deploy / collect a trap (xz units)"),
  // -- hunting: stalking --
  trophyTierMul: z.number().positive().optional().describe("rare-trophy odds multiplier per trophy tier (sim trophyChance; Keen Eye's tier 1 ≈ ×3)"),
  stalkingXp: z.record(z.string(), z.number().positive()).optional().describe("Stalking XP per clean take, keyed by prey KIND (npc doc id: rabbit/fox/deer)"),
  stalkingXpDefault: z.number().positive().optional().describe("Stalking XP for a prey kind not in stalkingXp"),
  trappingXpPerGame: z.number().positive().optional().describe("Trapping XP per small game landed in a haul"),
  huntsmanPerTake: z.number().nonnegative().optional().describe("Huntsman standing per stalked take"),
  huntsmanPerHaul: z.number().nonnegative().optional().describe("Huntsman standing per trap haul"),
  huntsmanPerTrophy: z.number().nonnegative().optional().describe("Huntsman standing per rare trophy taken"),
  // -- cooking: active (plan 25; defaults in src/sim/cooking.ts) --
  hearthRange: z.number().positive().optional().describe("max reach from a hearth (range deco / live campfire) to start a cook (xz units)"),
  cookCd: z.number().nonnegative().optional().describe("seconds between accepted cook starts (rate gate)"),
  cookXp: z.record(z.string(), z.number().positive()).optional().describe("cooking XP per dish, keyed by raw ingredient item type"),
  cookXpDefault: z.number().positive().optional().describe("cooking XP for an ingredient not in cookXp"),
  // -- cooking: the pan minigame (plan 25; defaults in src/sim/cooking.ts) --
  panArrowsBase: z.number().positive().optional().describe("pan prompts in the pattern at cook tier 0"),
  panArrowsPerTier: z.number().nonnegative().optional().describe("+ pan prompts per cook tier"),
  panGapBase: z.number().positive().optional().describe("seconds between pan prompts at tier 0"),
  panGapMin: z.number().positive().optional().describe("hard pan prompt-gap floor (playability)"),
  panSpeedRamp: z.number().nonnegative().optional().describe("pan gap divisor grows 1 + tier×this"),
  panHitWindow: z.number().positive().optional().describe("± seconds a pan press counts as a hit (× box panWindowMul)"),
  panMissBurn: z.number().positive().optional().describe("burn added per missed pan prompt (1.0 = burnt)"),
  panHitRelief: z.number().nonnegative().optional().describe("burn shed per hit pan prompt"),
  panInputSlack: z.number().nonnegative().optional().describe("seconds the server tolerates a panInput arriving late (latency grace)"),
  panDeadlinePad: z.number().nonnegative().optional().describe("seconds past the pan pattern's end before the server force-resolves"),
  cookTier: z.record(z.string(), z.number().int().nonnegative()).optional().describe("pan difficulty tier per raw ingredient (0..3, scales prompts + pace), keyed by item type"),
  cookTierDefault: z.number().int().nonnegative().optional().describe("pan difficulty tier for an ingredient not in cookTier"),
  // -- cooking: campfires --
  fireBurnSeconds: z.number().positive().optional().describe("seconds a started campfire burns before going out"),
  fireCount: z.number().int().positive().optional().describe("live campfires allowed per character"),
  fireRange: z.number().positive().optional().describe("max reach to start a campfire (xz units)"),
  // -- cooking: brewing (passive) --
  brewSoak: z.number().positive().optional().describe("default fermenter soak seconds (× box soakMul)"),
  brewCapacityMin: z.number().int().positive().optional().describe("min units per brew haul (uniform roll)"),
  brewCapacityMax: z.number().int().positive().optional().describe("max units per brew haul (uniform roll)"),
  brewCount: z.number().int().positive().optional().describe("deployed fermenters allowed at once (+ box brewCount)"),
  brewRange: z.number().positive().optional().describe("max reach to deploy / collect a fermenter (xz units)"),
  brewingXpPerUnit: z.number().positive().optional().describe("brewing XP per unit landed in a haul"),
  // -- cooking: patron deeds --
  cookPerDish: z.number().nonnegative().optional().describe("Cooks' Guild standing per plated dish"),
  cookPerBrew: z.number().nonnegative().optional().describe("Cooks' Guild standing per brew haul"),
  // -- chopping / tree-cutting (lightweight activity) --
  promptCount: z.number().int().positive().optional().describe("chop minigame prompts in the pattern"),
  gap: z.number().positive().optional().describe("seconds between chop prompts"),
  gapMin: z.number().positive().optional().describe("hard chop prompt-gap floor"),
  hitWindow: z.number().positive().optional().describe("± seconds a chop press counts as a hit"),
  missTension: z.number().min(0).max(1).optional().describe("fail-meter added per missed chop (1.0 = session fails)"),
  hitRelief: z.number().min(0).max(1).optional().describe("fail-meter shed per hit chop"),
  chopStaCost: z.number().nonnegative().optional().describe("stamina spent per judged chop prompt (hit or miss)"),
  inputSlack: z.number().nonnegative().optional().describe("seconds the server tolerates a chopInput arriving late"),
  deadlinePad: z.number().nonnegative().optional().describe("seconds past the chop pattern's end before force-resolve"),
  sessionTimeout: z.number().positive().optional().describe("seconds a tree lock lasts before auto-release if the cutter stalls"),
});

export type TunablesDoc = z.infer<typeof TunablesSchema>;

// Registry --------------------------------------------------------------------
// Domain name → schema. gen-content.ts walks this to emit JSON Schemas; each
// domain's directory under content/ matches the plural key used in the manifest.

/** Per-doc content domains: directory name → its schema. THE shared table the
 *  content API (server), the studio's generic Content editor, and anything
 *  else mapping dirs↔schemas derive from — so adding a per-doc domain stays a
 *  schemas.ts-only change on this axis. `world`/`anim` are mixed-schema
 *  singleton dirs, handled specially by their consumers. */
export const DOC_DOMAIN_SCHEMA = {
  quests: "quest",
  npcs: "npc",
  items: "item",
  models: "model",
  decos: "deco",
  parts: "part",
  structures: "structure",
  shops: "shop",
  skills: "skill",
  standing: "standing",
  moves: "move",
  scenarios: "scenario",
  cinematics: "cinematic",
  "catch-tables": "catch-table",
  "scatter-palettes": "scatter-palette",
  tunables: "tunables",
  maps: "map",
} as const;

export const SCHEMAS = {
  quest: QuestSchema,
  model: ModelSchema,
  item: ItemSchema,
  shop: ShopSchema,
  skill: SkillSchema,
  standing: StandingSchema,
  npc: NpcSchema,
  deco: DecoSchema,
  part: PartGroupSchema,
  structure: StructureDocSchema,
  move: MoveSchema,
  scenario: ScenarioSchema,
  cinematic: CinematicSchema,
  "catch-table": CatchTableSchema,
  "scatter-palette": ScatterPaletteSchema,
  tunables: TunablesSchema,
  map: MapSchema,
  "world-factions": WorldFactionsSchema,
  "anim-clips": AnimClipsSchema,
} as const;

export type DomainName = keyof typeof SCHEMAS;
